import { ReactNode } from 'react'

import { DunsSearchFieldsNames, DUNSSearchType } from '@/models/back-office-pages/duns'
import {
  DownloadableFileDTO,
  InvoiceExternalVerificationStatusFilter,
  InvoiceReportFileDTO,
  InvoiceReportResponseDTO,
  InvoiceStatus,
  InvoiceStatusFilter,
  PageWithTotals_InvoiceDTO_InvoiceTotalsDTO,
  SettlementDilutionType,
} from '@/models/back-office-pages/invoices'
import { LoanBookSecurityMeasureDTO, LoanBookType } from '@/models/back-office-pages/loan-book'
import { AccountingNoteStatus, FileFormat, NoteType } from '@/models/back-office-pages/notes'
import { OutstandingVerificationStatus } from '@/models/back-office-pages/operation-tasks'
import { AgreementDocumentType, NewOrExistingType, PaymentHistoryDocumentType } from '@/models/client-pages/debtors'
import { ReportTypeClientPortal } from '@/models/client-pages/reports'
import {
  AdministrationTypes,
  Currency,
  FromToFilterInput,
  LimitsDTO,
  MinMaxFilterInput,
  ReportFileDTOInput,
  ReportPeriod,
  RepresentativeCompanyDTO,
  RepresentativeDTO,
  SourcingAgentTypes,
} from '@/models/common'

import { CountryCodes } from '../countries'
import { Direction } from '../table'
import {
  DebtorBlockInfoDTO,
  DebtorCommentDTO,
  DebtorCommentListDTO,
  DebtorDetailsInfoDTO,
  DebtorDunsDetailsDTO,
} from './debtors'
import {
  AmlRisk,
  DebtorStatementInvoice,
  DebtorStatementType,
  FactoringProduct,
  FundingLine,
  ProspectReportFileDTO,
  RiskOfferAuditDTO,
  RiskOfferDebtorDTO,
  RiskOfferDTO,
  RiskOfferInvoiceVerificationDTO,
} from './prospects'
import { CommissionType, SourcingAgentListDTO } from './system'

export enum FABClientStatus {
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  APPROVED = 'approved',
  INIT = 'init',
  MIGRATED_TO_SPV = 'migrated_to_spv',
}

export enum ClientFilterStatus {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
  APPROVED = 'approved',
  INIT = 'init',
  MIGRATED_TO_SPV = 'migrated_to_spv',
}

export interface ClientListDTO {
  financingLimit: number
  amlRisk: AmlRisk
  availableFinancingLimit: number
  clientId: string
  clientStatus: FABClientStatus
  companyCountry: string
  companyName: string
  registrationNumber: string
  sourcingAgentName: string
  sourcingAgentAdministrates: string
  usedLimit: number
  invoiceAttachmentRequired: boolean
  invoiceAttachmentRequiredForLoanBook: boolean
}

export interface ClientTotalsDTO {
  totalAvailableFinancingLimit: number
  totalUsedLimit: number
  totalLimit: number
}

export interface Page_ClientListDTO {
  content: Array<ClientListDTO>
  totals: ClientTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientsData {
  clientList: Page_ClientListDTO
}

export interface ClientsVariables {
  companyName?: string
  registrationNumber?: string
  availableFinancingLimit?: MinMaxFilterInput
  usedLimit?: MinMaxFilterInput
  pageSize?: number
  pageNum?: number
  tableStatus: ClientFilterStatus
  clientStatus?: ClientFilterStatus[]
  sortDirection?: Direction
  sortField?: string
}

export interface DuplicateClientData {
  duplicateClient: string
}

export interface DuplicateClientFormValues {
  sourcingAgentId: string
}

export interface DuplicateClientVariables extends DuplicateClientFormValues {
  clientId: string
}

export enum DuplicateClientFieldsNames {
  SA_ID = 'sourcingAgentId',
}

export enum CopyClientFromProductionFieldsNames {
  SOURCING_AGENT_ID = 'sourcingAgentId',
}

export interface CopyClientFromProductionData {
  copyClientFromProduction: {
    clientId: string
    companyName: string
    debtorIds: string[]
  }
}

export interface CopyClientFromProductionFormValues {
  [DunsSearchFieldsNames.DUN_ID]: string
  [DunsSearchFieldsNames.SEARCH_TYPE]: DUNSSearchType
  [DunsSearchFieldsNames.COUNTRY_CODE]: string
  [CopyClientFromProductionFieldsNames.SOURCING_AGENT_ID]: string
}

export interface CopyClientFromProductionVariables {
  dunsId: string
  sourcingAgentId: string
}

export enum ClientReportStatus {
  CANCELED = 'CANCELED',
  ERROR = 'ERROR',
  PENDING = 'PENDING',
  READY = 'READY',
}

export interface ClientFullReportFileDTO {
  documentId: string
  fileName: string
  createdOn: Date
  status: ClientReportStatus
  uploader: string
}

interface Page_ClientFullReportFileDTO {
  content: ClientFullReportFileDTO[]
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientsReportsData {
  clientReportsList: Page_ClientFullReportFileDTO
}

export interface ClientsReportsVariables {
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  createdOn?: FromToFilterInput
}

export interface ClientReportResponseDTO {
  reportId: string
}
export interface RequestGenerateClientsReportData {
  clientReportRequest: ClientReportResponseDTO
}

export interface RequestGenerateClientsReportVariables {
  availableFinancingLimit?: MinMaxFilterInput
  sourcingAgentAdministrates?: [AdministrationTypes]
  usedLimit?: MinMaxFilterInput
  registrationNumber?: string
  companyName?: string
  clientStatus?: [ClientFilterStatus]
  tableStatus: ClientFilterStatus
}

export interface DownloadClientsReportData {
  downloadClientReport: InvoiceReportFileDTO
}

export interface DownloadClientsReportVariables {
  documentId: string
}

export interface CancelClientsReportData {
  cancelClientReport: InvoiceReportResponseDTO
}

export interface CancelClientsReportVariables {
  reportId: string
}

export interface SourcingAgentListForClientDuplicatesData {
  sourcingAgentListForClientDuplicates: Array<SourcingAgentListDTO>
}

export interface SourcingAgentListForClientDuplicatesVariables {
  clientId: string
}
export interface ClientsSelectData {
  clientSelectFilter: ClientListDTO[]
}

export interface ClientsSelectVariables {
  companyName: string
  filterOnlyApprovedNotBlocked?: boolean
  companyCountry?: string
}

export interface ClientModelAndFeesDTO {
  invoiceAttachmentRequired: boolean
  enablePartialInvoiceFactoring: boolean
  invoiceAttachmentRequiredForLoanBook: boolean
  enableSemiAutomaticFunding: boolean
  generatePnCnAsVatInvoice: boolean
  clientUsesGAmount: boolean
  peppolSendEnabled: boolean
  fraudDetected: boolean
  clientFraudDetected: boolean
  spv: boolean
}

export interface ClientSalesInformationDTO {
  brokerCommission: number
  brokerId: string
  brokerName: string
  commissionType: CommissionType
  clientManager: string
  clientManagerEmail: string
  channel: Channel
  commissionEndDate: string
  commissionPeriod: number
  overrideInterestFees: number
  overrideOnboardingFees: number
}

export interface ClientDunsDetailsDTO {
  city: string
  country: string
  dunReportCreated: Date
  dunsId: string
  hooversIndustryCode: string
  naceIndustryCode: string
  numberOfEmployees: number
  postalCode: string
  rawData: string
  registrationNumber: string
  standardIndustryCode: string
  startDate: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
  phoneNumbers?: string
}

export interface ClientCommunicationPreferencesDTO {
  creditNoteNotifications: Array<string>
  generalNotifications: Array<string>
  language: string
  clientWantsToBeNotifiedOfDebtorVerificationEmails: boolean
}

export interface ClientCommunicationPreferencesFormValues extends ClientCommunicationPreferencesDTO {
  isClientPortal?: boolean
}

export interface ClientCompanyDetailsDTO {
  companyName?: string
  beneficiary: string
  beneficiaries: Array<ClientBeneficiaryDTO>
  correspondenceCountry: string
  correspondenceEmail: string
  correspondenceName: string
  correspondencePhone: string
  correspondencePlace: string
  correspondencePostalCode: string
  correspondenceStreetLine1: string
  correspondenceStreetLine2: string
  iban: string
  legalStructureId: string
  legalStructure: string
  registrationNumber: string
  vatNumber: string
  legalRepresentative: string
  legalRepresentativeRole: string
}

export interface ClientCompanyDetails extends ClientCompanyDetailsDTO {
  clientStatus?: FABClientStatus
  companyName?: string
  sourcingAgentAdministrates?: string
  sourcingAgentName?: string
}

export interface ClientFinancingLimitDTO {
  currency: Currency
  availableLimit: number
  limit: number
  limitExpiry: string
  usedLimit: number
}

export interface ClientApprovalDTO {
  approvalComment: string
  approvedByEmail: string
  approvedById: string
  approvedByName: string
  updatedOn: Date
}

export interface ClientApprovalChecklistDTO {
  hasActiveContracts: boolean
  hasCommunicationPreferences: boolean
  hasCompanyDetails: boolean
  hasFactoringSettings: boolean
  hasSalesInformation: boolean
  hasFinancingLimit: boolean
  __typename: string
}

export interface ClientRiskAlertDTO {
  blockPnPayments: boolean
  comment: string
  blockCnPayments: boolean
  blockLoanBookPayments: boolean
  disableClientActions: boolean
  blockVnGeneration: boolean
  disableLoanBookVatNotesGeneration: boolean
}

export interface ClientLogDTO {
  clientId: string
  currency: Currency
  cnPaymentsDisabled: boolean
  createdAt: Date
  event: AuditActions
  financingLimit: number
  financingLimitExpiry: Date
  id: string
  pnPaymentsDisabled: boolean
  riskAlertsComments: string
  userFullName: string
  companyName: string
  amlRisk: AmlRisk
}

export interface ClientLoanBookLimitDTO {
  creditLineTotalAmount: number
  creditLineUsedAmount: number
  creditLineAvailableAmount: number
  loanUsedAmount: number
  loanTotalAmount: number
  loanAvailableAmount: number
  reverseFactoringTotalAmount: number
  reverseFactoringUsedAmount: number
  reverseFactoringAvailableAmount: number
}

export enum LoanBookLimitFieldNames {
  CREDIT_LINE_TOTAL = 'creditLineTotalAmount',
  CREDIT_LINE_USED = 'creditLineUsedAmount',
  CREDIT_LINE_AVAILABLE = 'creditLineAvailableAmount',
  LOAN_TOTAL = 'loanTotalAmount',
  LOAN_USED = 'loanUsedAmount',
  LOAN_AVAILABLE = 'loanAvailableAmount',
  REVERSE_FACTORING_TOTAL = 'reverseFactoringTotalAmount',
  REVERSE_FACTORING_USED = 'reverseFactoringUsedAmount',
  REVERSE_FACTORING_AVAILABLE = 'reverseFactoringAvailableAmount',
}

export interface ClientOperationsQuestionnaireForSecurityMeasureDTO {
  id: string
  securityMeasureSubtype: FactoringSecurityMeasureType
  securityMeasureType: FactoringSecurityMeasureOption
  checked: boolean
}

export interface ClientOperationsQuestionnaireDTO {
  commercialTermsApplyDebtors: boolean
  commercialTermsApplyDebtorsComment: string
  contractDocumentSignedByAuthorizedPerson: boolean
  creditManagementPlanAppliesDebtors: boolean
  creditManagementPlanAppliesDebtorsComment: string
  feesSettlementsMatchesContract: boolean
  financingLimitMatchesContract: boolean
  invoiceVerificationRulesApplyDebtors: boolean
  invoiceVerificationRulesApplyDebtorsComment: string
  requirementsBeforeFinancingReviewed: boolean
  securityMeasureQuestions: Array<ClientOperationsQuestionnaireForSecurityMeasureDTO>
  securityMeasuresSignedAndUploaded: boolean
  signedContractMatchesDetails: boolean
  noDebtorStatementIsRequired: boolean
  noDebtorStatementIsRequiredComment: string
  maximumPaymentTermCompliesWithRisk: boolean
  maximumPaymentTermCompliesWithRiskComment: string
  userInviteSent: boolean
}

export interface ClientScheduledReportDTO {
  id: string
  recipients: string[]
  reportPeriod: ReportPeriod
  reportType: ReportTypeClientPortal
  startDate: string
}

export enum ScheduledReportsFieldNames {
  SCHEDULED_REPORTS = 'scheduledReports',
}

export enum ScheduledReportItemFieldNames {
  ID = 'id',
  REPORT_TYPE = 'reportType',
  REPORT_PERIOD = 'reportPeriod',
  START_DATE = 'startDate',
  RECIPIENTS = 'recipients',
}

export interface ScheduledReportItemFormValues {
  [key: string]: string | string[] | null
  [ScheduledReportItemFieldNames.ID]: string
  [ScheduledReportItemFieldNames.REPORT_TYPE]: ReportTypeClientPortal | null
  [ScheduledReportItemFieldNames.REPORT_PERIOD]: ReportPeriod | null
  [ScheduledReportItemFieldNames.START_DATE]: string | null
  [ScheduledReportItemFieldNames.RECIPIENTS]: string[]
}

export interface ScheduledReportsFormValues {
  scheduledReports: ScheduledReportItemFormValues[]
}

export interface UpdateScheduledReportsData {
  updateClientScheduledReports: ClientCardDetailsDTO
}

export interface UpdateScheduledReportsVariables {
  clientId: string
  scheduledReports: ScheduledReportItemFormValues[]
}

export interface ClientCardDetailsDTO {
  amlRisk: AmlRisk
  companyName: string
  creditRiskManagerEmail: string
  clientStatus: FABClientStatus
  sourcingAgentAdministrates: AdministrationTypes
  sourcingAgentName: string
  approval: ClientApprovalDTO
  approvalChecklist: ClientApprovalChecklistDTO
  communicationPreferences: ClientCommunicationPreferencesDTO
  companyDetails: ClientCompanyDetailsDTO
  dunsDetails: ClientDunsDetailsDTO
  financingLimitPLN: LimitsDTO
  financingLimits: Array<LimitsDTO>
  financingLimitsPln: Array<LimitsDTO>
  loanBookLimit: ClientLoanBookLimitDTO
  modelAndFees: ClientModelAndFeesDTO
  salesInformation: ClientSalesInformationDTO
  riskAlerts: ClientRiskAlertDTO
  operationsQuestionnaire: ClientOperationsQuestionnaireDTO
  riskRequiredSecurityMeasures: boolean
  scheduledReports: Array<ClientScheduledReportDTO>
  logs: ClientLogDTO[]
}

export interface ClientDetailsData {
  clientCardDetails: ClientCardDetailsDTO
}

export interface ClientDetailsVariables {
  clientId: string
}

export interface UpdateModelsAndFeesData {
  updateClientModelsAndFees: ClientCardDetailsDTO
}

export interface UpdateModelsAndFeesVariables {
  clientId: string
  invoiceAttachmentRequired: boolean
  enablePartialInvoiceFactoring: boolean
  clientUsesGAmount: boolean
  peppolSendEnabled: boolean
  enableSemiAutomaticFunding: boolean
  generatePnCnAsVatInvoice: boolean
}

export enum ModelsAdnFeesFieldNames {
  REQUIRED_INVOICE = 'invoiceAttachmentRequired',
  ENABLE_PARTIAL_INVOICE_FACTORING = 'enablePartialInvoiceFactoring',
  PEPPOL_SEND_ENABLED = 'peppolSendEnabled',
  ENABLE_G_AMOUNT = 'clientUsesGAmount',
  ENABLE_SEMI_AUTOMATIC_FUNDING = 'enableSemiAutomaticFunding',
  GENERATE_PN_CN_AS_VAT_INVOICE = 'generatePnCnAsVatInvoice',
}

export interface UpdateClientOperationsQuestionnairesData {
  updateClientOperationsQuestionnaire: ClientCardDetailsDTO
}

export interface UpdateClientOperationsQuestionnairesVariables {
  clientId: string
  requirementsBeforeFinancingReviewed: boolean
  contractDocumentSignedByAuthorizedPerson: boolean
  signedContractMatchesDetails: boolean
  financingLimitMatchesContract: boolean
  feesSettlementsMatchesContract: boolean
  commercialTermsApplyDebtors: boolean
  commercialTermsApplyDebtorsComment: string
  invoiceVerificationRulesApplyDebtors: boolean
  invoiceVerificationRulesApplyDebtorsComment: string
  creditManagementPlanAppliesDebtors: boolean
  userInviteSent: boolean
  creditManagementPlanAppliesDebtorsComment: string
  securityMeasuresSignedAndUploaded: boolean | null
  securityMeasureQuestions: Array<ClientOperationsQuestionnaireForSecurityMeasureDTO>
}

export enum UpdateClientOperationsQuestionnairesFieldNames {
  REQUIREMENTS_BEFORE_FINANCING_REVIEWED = 'requirementsBeforeFinancingReviewed',
  CONTRACT_SIGNED_BY_AUTHORIZED_PERSON = 'contractDocumentSignedByAuthorizedPerson',
  SIGNED_CONTRACT_MATCHES_DETAILS = 'signedContractMatchesDetails',
  FINANCING_LIMIT_MATCHES_CONTRACT = 'financingLimitMatchesContract',
  FEES_SETTLEMENTS_MATCHES_CONTRACT = 'feesSettlementsMatchesContract',
  COMMERCIAL_TERMS_APPLY_DEBTORS = 'commercialTermsApplyDebtors',
  COMMERCIAL_TERMS_APPLY_DEBTORS_COMMENT = 'commercialTermsApplyDebtorsComment',
  INVOICE_VERIFICATION_RULES_APPLY_DEBTORS = 'invoiceVerificationRulesApplyDebtors',
  INVOICE_VERIFICATION_RULES_APPLY_DEBTORS_COMMENT = 'invoiceVerificationRulesApplyDebtorsComment',
  CMP_APPLY_DEBTORS = 'creditManagementPlanAppliesDebtors',
  CMP_APPLY_DEBTORS_COMMENT = 'creditManagementPlanAppliesDebtorsComment',
  SECURITY_MEASURES_SIGNED_AND_UPLOADED = 'securityMeasuresSignedAndUploaded',
  SECURITY_MEASURE_OPTIONS = 'securityMeasureQuestions',
  USER_INVITE_SENT = 'userInviteSent',
  MAX_PAYMENT_TERM_COMPLIES_RISK_DECISION = 'maximumPaymentTermCompliesWithRisk',
  MAX_PAYMENT_TERM_COMPLIES_RISK_DECISION_COMMENT = 'maximumPaymentTermCompliesWithRiskComment',
  DS_SIGNED_BY_AUTHORIZED_PERSON = 'noDebtorStatementIsRequired',
  DS_SIGNED_BY_AUTHORIZED_PERSON_COMMENT = 'noDebtorStatementIsRequiredComment',
}

export enum SecurityMeasureFieldArrayNames {
  ID = 'id',
  CHECKED = 'checked',
  SECURITY_MEASURE_TYPE = 'securityMeasureType',
  SECURITY_MEASURE_SUBTYPE = 'securityMeasureSubtype',
}

export interface UpdateRiskAlertsData {
  updateClientRiskAlerts: ClientCardDetailsDTO
}

export interface UpdateRiskAlertsVariables {
  clientId: string
  blockCnPayments: boolean
  blockPnPayments: boolean
  blockLoanBookPayments: boolean
  blockVnGeneration: boolean
  comment: string
}

export enum RiskAlertsFieldNames {
  CN_PAYMENTS_BLOCKED = 'blockCnPayments',
  PN_PAYMENTS_BLOCKED = 'blockPnPayments',
  VN_GENERATION_BLOCKED = 'blockVnGeneration',
  DISABLE_CLIENT_ACTIONS = 'disableClientActions',
  LOAN_BOOK_PAYMENTS_BLOCKED = 'blockLoanBookPayments',
  LOAN_BOOK_VN_GENERATION_BLOCKED = 'disableLoanBookVatNotesGeneration',
  PEPOOL_DISABLED_FOR_DEBTOR = 'peppolDisabledForDebtor',
  DISABLE_AUTO_FUNDING = 'autoFundingDisable',
  COMMENT = 'comment',
}

export enum ClientDocumentFileType {
  ANNUAL_AND_INTERIM_FINANCIALS = 'ANNUAL_AND_INTERIM_FINANCIALS',
  ONDATO_API = 'ONDATO_API',
  RPA = 'RPA',
  SECURITY_MEASURE = 'SECURITY_MEASURE',
  WISE_FUNDING_REPORT = 'WISE_FUNDING_REPORT',
  OTHER = 'OTHER',
}

export interface ClientDocumentDTO {
  clientId: string
  content: string
  description: string
  reportCreatedAt: string
  reportType: ClientDocumentFileType
  fileName: string
  documentId: string
  uploader: string
}

export interface Page_ClientDocumentDTO {
  content: Array<ClientDocumentDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientReportsData {
  clientDocumentList: Page_ClientDocumentDTO
}

export interface ClientReportsVariables {
  clientId: string
  fileName?: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadClientReportData {
  downloadClientDocument: ReportFileDTOInput
}

export interface DownloadClientReportVariables {
  documentId: string
}

export interface UploadClientReportData {
  uploadClientDocument: string
}

export interface UploadClientReportVariables {
  reportType: ClientDocumentFileType | string
  clientId?: string
  file: ReportFileDTOInput | string
  reportCreatedAt: string
  description?: string
}

export interface UploadClientReportFormValues {
  reportType: ClientDocumentFileType | string
  clientId?: string
  files: Array<ReportFileDTOInput>
  reportCreatedAt: string
  description?: string
}

export interface DeleteClientReportData {
  deleteClientDocument: ReportFileDTOInput
}

export interface DeleteClientReportVariables {
  documentId: string
}

export interface UpdateSalesInformationData {
  updateClientSalesInformation: ClientCardDetailsDTO
}

export enum Channel {
  BROKER = 'BROKER',
  MARKETING = 'MARKETING',
  SALES = 'SALES',
}

export interface UpdateSalesInformationFormVariables {
  clientManager: string
  clientManagerEmail: string
  channel: Channel
  brokerId: string
  overrideBrokerFee?: boolean
  overrideIncreasedFees?: number
  overrideOnboardingFees?: number
  commissionPeriod?: number
  commissionEndDate?: string
}

export interface UpdateSalesInformationVariables {
  clientId: string
  clientManager: string
  clientManagerEmail: string
  channel: Channel
  brokerId: string
  overrideIncreasedFees?: number
  overrideOnboardingFees?: number
  commissionPeriod?: number
  commissionEndDate?: string
}

export enum SalesInformationFieldNames {
  CHANNEL = 'channel',
  CLIENT_MANAGER = 'clientManager',
  CLIENT_MANAGER_EMAIL = 'clientManagerEmail',
  BROKER_ID = 'brokerId',
  BROKER_NAME = 'brokerName',
  COMMISSION_END_DATE = 'commissionEndDate',
  COMMISSION_PERIOD = 'commissionPeriod',
  OVERRIDE_BROKER_FEE = 'overrideBrokerFee',
  OVERRIDE_INTEREST_FEE = 'overrideInterestFees',
  OVERRIDE_ONBOARDING_FEE = 'overrideOnboardingFees',
}

export interface UpdateClientCommunicationPreferencesData {
  updateClientCommunicationPreferences: ClientCardDetailsDTO
}

export interface UpdateClientCommunicationPreferencesVariables {
  clientId: string
  creditNoteNotifications: Array<string>
  generalNotifications: Array<string>
  language: string
  clientWantsToBeNotifiedOfDebtorVerificationEmails: boolean
}

export enum ClientCommunicationPreferencesFieldNames {
  CREDIT_NOTE_NOTIFICATIONS = 'creditNoteNotifications',
  GENERAL_NOTIFICATIONS = 'generalNotifications',
  LANGUAGE = 'language',
  NOTIFY_ABOUT_VERIFICATION = 'clientWantsToBeNotifiedOfDebtorVerificationEmails',
  IS_CLIENT_PORTAL = 'isClientPortal',
}

export interface UpdateClientDunsDetailsData {
  updateClientDunsDetails: ClientCardDetailsDTO
}

export interface UpdateClientDunsDetailsVariables {
  clientId: string
}

export enum FinancingLimitFieldNames {
  TOTAL_LIMIT = 'financingLimit',
  EXPIRY_DATE = 'financingLimitExpiry',
  USED_LIMIT = 'usedLimit',
  AVAILABLE_LIMIT = 'availableFinancingLimit',
}

export interface UpdateFinancingLimitData {
  updateClientFinancingLimit: ClientCardDetailsDTO
}

export interface UpdateFinancingLimitVariables {
  clientId: string
  financingLimitExpiry: string
  financingLimit: number
  currency: Currency
}

export interface UpdateFinancingLimitsData {
  updateClientFinancingLimits: ClientCardDetailsDTO
}

export enum UpdateLimitsFieldArrayNames {
  FUNDING_LINE = 'fundingLine',
  LIMIT = 'limit',
  EXPIRY_DATE = 'limitExpiry',
  USED_LIMIT = 'usedLimit',
  AVAILABLE_LIMIT = 'availableLimit',
  CURRENCY = 'currency',
}

export enum UpdateLimitsFieldsNames {
  LIMITS = 'limits',
}

export interface ClientFinancingLimitByFundingLineDTOInput {
  amount: number
  currency: Currency
  financingLimitExpiry: string
  fundingLineType: FundingLine
}

export interface UpdateFinancingLimitsVariables {
  clientId: string
  limits: ClientFinancingLimitByFundingLineDTOInput[]
}

export interface UpdateLimitsFormValues {
  limits: Array<LimitsDTO>
}

export interface ClarificationCommentDTO {
  comment: string
  createdOn: string
  fullName: string
  usersLogin: string
}

export interface ClientClarificationDTO {
  closed: boolean
  comment: string
  createdOn: string
  explanations: Array<ClarificationCommentDTO>
  fullName: string
  usersLogin: string
  id: string
}

export interface AddClientClarificationCommentData {
  addClientClarificationComment: Array<ClientClarificationDTO>
}

export interface AddClientClarificationCommentVariables {
  clientClarificationId: string
  comment: string
}

export interface AddClientClarificationData {
  addClientClarification: ClientClarificationDTO
}

export interface AddClientClarificationVariables {
  clientId: string
  comment: string
  notifyEmail: string
}

export enum AddNewClientClarificationFieldsNames {
  COMMENT = 'comment',
  EMAIL = 'notifyEmail',
}

export interface ClientClarificationsData {
  clientClarifications: Array<ClientClarificationDTO>
}

export interface ClientClarificationsVariables {
  clientId: string
}

export interface BackOfficeUserDTO {
  email: string
  id: string
  firstName: string
  lastName: string
}

export interface GetBackofficeColleaguesData {
  findAllBackOfficeColleagues: Array<BackOfficeUserDTO>
}

export interface UpdateClientCompanyDetailsData {
  updateClientCompanyDetails: ClientCardDetailsDTO
}

export interface UpdateClientCompanyVariables {
  clientId: string
  correspondenceStreetLine1: string
  correspondenceStreetLine2: string
  correspondencePlace: string
  correspondencePostalCode: string
  correspondenceCountry: string
  legalStructureId: string
  beneficiary: string
  iban: string
  registrationNumber: string
  vatNumber: string
  correspondenceName: string
  correspondencePhone: string
  correspondenceEmail: string
  legalRepresentative: string
  legalRepresentativeRole: string
  beneficiaries: Array<ClientBeneficiaryDTO>
}

export enum ClientCompanyDetailsFieldNames {
  STATUS = 'clientStatus',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  SOURCING_AGENT = 'sourcingAgentName',
  COMPANY_NAME = 'companyName',
  FULL_ADDRESS = 'fullAddress',
  STREET_1 = 'correspondenceStreetLine1',
  STREET_2 = 'correspondenceStreetLine2',
  CITY = 'correspondencePlace',
  POSTAL_CODE = 'correspondencePostalCode',
  COUNTRY = 'correspondenceCountry',
  ADDRESS_VERIFIED = 'addressVerified',
  ADDRESS_VERIFIED_BY = 'addressVerifiedBy',
  LEGAL_STRUCTURE = 'legalStructureId',
  LEGAL_STRUCTURES = 'legalStructures',
  BENEFICIARY = 'beneficiary',
  IBAN = 'iban',
  BANK_ACCOUNTS = 'beneficiaries',
  COC = 'registrationNumber',
  VAT = 'vatNumber',
  NAME = 'correspondenceName',
  PHONE = 'correspondencePhone',
  EMAIL = 'correspondenceEmail',
  LEGAL_REPRESENTATIVE = 'legalRepresentative',
  LEGAL_REPRESENTATIVE_ROLE = 'legalRepresentativeRole',
}

export interface LegalStructureTypesDTO {
  countryCode: string
  id: string
  name: string
}

export interface LegalStructuresData {
  listLegalStructures: Array<LegalStructureTypesDTO>
}

export interface LegalStructuresVariables {
  clientId: string
}

export enum ClientDebtorStatus {
  ACTIVE = 'active',
  APPROVED = 'approved',
  INACTIVE = 'inactive',
  INIT = 'init',
  BLOCKED = 'blocked',
}

export enum DebtorTaskStatus {
  COMPLETED = 'COMPLETED',
  NOT_STARTED = 'NOT_STARTED',
  PENDING = 'PENDING',
}

export enum RiskTaskStatus {
  COMPLETED = 'COMPLETED',
  PENDING = 'PENDING',
}

export enum OpsTaskStatus {
  NOT_STARTED = 'NOT_STARTED',
  PENDING = 'PENDING',
}

export interface ClientDebtorDTO {
  currency: Currency
  addedByClient: boolean
  createdOn: Date
  available: number
  clientName: string
  companyName: string
  contractLabel: string
  contractName: string
  country: string
  creditLimit: number
  creditManagement: string
  id: string
  registrationNumber: string
  status: ClientDebtorStatus
  relationshipStatus: ClientDebtorRelationshipStatus
  usedLimit: number
  vat: string
  purchaseRate: number
  invoiceAttachmentRequired: boolean
  enablePartialFactoring: boolean
  factoringProduct: FactoringProduct
  sourcingAgentAdministrates: string
  opsTaskStatus: DebtorTaskStatus
  riskTaskStatus: DebtorTaskStatus
}

export interface ClientDebtorTotalsDTO {
  totalAvailableLimit: number
  totalCreditLimit: number
  totalUsedLimit: number
  totalAvailableLimitPLN: number
  totalCreditLimitPLN: number
  totalUsedLimitPLN: number
}

export interface Page_ClientDebtorDTO {
  content: Array<ClientDebtorDTO>
  totals: ClientDebtorTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientDebtorsData {
  clientDebtorList: Page_ClientDebtorDTO
}

export interface ClientDebtorsVariables {
  clientId: string
  companyName?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface ClientDebtorsSelectData {
  filterClientDebtors: Array<ClientDebtorDTO>
}

export interface ClientDebtorsSelectVariables {
  clientId: string
  searchText: string
  filterOnlyApprovedNotBlocked?: boolean
}

export enum ContractStatus {
  ACTIVE = 'ACTIVE',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  RENEWED = 'RENEWED',
  NEW = 'NEW',
}

export interface ContractsDTO {
  currency: Currency
  administrationFee: number
  contractStatus: ContractStatus
  contractTypeId: string
  contractTypeName: string
  dailyFee: number
  daysIncluded: number
  duration: number
  factoringFee: number
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  gracePeriod: number
  endDate: string
  id: string
  contractId?: string
  increasedFee: number
  nonRecourseFee: number
  paymentTerm: number
  signingDate: string
  sourcingAgentAdministrates: SourcingAgentTypes
  contractName: string
  transferFee: number
}

export interface Page_ContractsDTO {
  content: Array<ContractsDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface FactoringContractsData {
  contractList: Page_ContractsDTO
}

export interface FactoringContractsVariables {
  clientId: string
  contractTypeName?: string
  duration?: MinMaxFilterInput
  endDate?: FromToFilterInput
  signingDate?: FromToFilterInput
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
  contractStatus?: ContractStatus[]
}

export enum FeeBasis {
  FACTOR_AMOUNT = 'factor_amount_based',
  OPEN_AMOUNT = 'open_amount_based',
}

export enum ContractEditReason {
  AMENDMENT = 'AMENDMENT',
  MISTAKE = 'MISTAKE',
  RENEWAL = 'RENEWAL',
}

export enum OnboardingFeeType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE_OF_FINANCING_LIMIT = 'PERCENTAGE_OF_FINANCING_LIMIT',
}

export enum RenewalFeeType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE_OF_FINANCING_LIMIT = 'PERCENTAGE_OF_FINANCING_LIMIT',
}

export enum InsuranceType {
  ALLIANZ = 'ALLIANZ',
  ATRADIUS = 'ATRADIUS',
}

export interface ContractFeeDetailsDTO {
  currency: Currency
  contractId?: string
  contractStatus?: ContractStatus
  contractTypeName: string
  contractName: string
  contractTypeId: string
  fundingLine: FundingLine | string
  factoringProduct: FactoringProduct
  sourcingAgentAdministrates?: AdministrationTypes

  // Commercial Terms
  signingDate: string
  endDate: string
  duration: number
  financingLimit: number // Client -financing limit, Prospect - contracted limit
  financingLimitPLN: number // Client -financing limit, Prospect - contracted limit
  contractedVolume: number
  purchaseRate: number
  paymentTerm: number
  //Insurance
  insuredRate: string
  getgAmountInsured?: boolean // Client only
  insuranceType?: InsuranceType // Prospect only
  // Factoring Fees
  factoringFee: number
  daysIncluded: number
  dailyFee: number
  monthlyFee: number
  gracePeriod: number
  increasedFee: number
  administrationFee: number
  transferFee?: number // Prospect only
  nonRecourseFee: number
  nonRecourseFeeMinimum: number
  // Other fees
  creditLimitUpdateSettlementFee: number
  onBoardingFee: number
  onboardingFeeType: OnboardingFeeType
  onboardingFeeInPercentage: number
  renewalFeeType: RenewalFeeType
  renewalFee: number
  renewalFeeInPercentage: number
  financingIncreaseFee: number
  financingIncreaseFeeMinimum: number
  penaltyOnVolume: number
  contractEditReason: ContractEditReason // Client only
  addSettlement?: boolean // Client only

  // Bank account
  beneficiary?: string // Client only
  iban?: string // Client only
  bankAccount?: { beneficiary: string; iban: string } // Client only
}

export interface ContractDetails extends ContractFeeDetailsDTO {
  sourcingAgentAdministrates?: AdministrationTypes
  clientUsesGAmount?: boolean
}

export enum NewContractFieldNames {
  CONTRACT_NAME = 'contractName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  CONTRACT_TYPE_ID = 'contractTypeId',
  CONTRACT_TYPE_NAME = 'contractTypeName',
  CURRENCY = 'currency',
  FUNDING_LINE = 'fundingLine',
  FACTORING_PRODUCT = 'factoringProduct',
  START_DATE = 'signingDate',
  END_DATE = 'endDate',
  DURATION = 'duration',
}

export enum DuplicateContractFieldNames {
  CONTRACT_NAME = 'contractName',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  CONTRACT_TYPE_ID = 'contractTypeId',
  CURRENCY = 'currency',
  FUNDING_LINE = 'fundingLine',
  FACTORING_PRODUCT = 'factoringProduct',
  START_DATE = 'signingDate',
  END_DATE = 'endDate',
  DURATION = 'duration',
}

export enum ContractFieldNames {
  CURRENCY = 'currency',
  SOURCING_AGENT_ADMINISTRATES = 'sourcingAgentAdministrates',
  STATUS = 'contractStatus',
  CONTRACT_TYPE_ID = 'contractTypeId',
  CONTRACT_TYPE_NAME = 'contractTypeName',
  FUNDING_LINE = 'fundingLine',
  FACTORING_PRODUCT = 'factoringProduct',
  START_DATE = 'signingDate',
  END_DATE = 'endDate',
  DURATION = 'duration',
  VOLUME_COMMITMENT = 'contractedVolume',
  ADVANCED_RATE = 'purchaseRate',
  PAYMENT_TERM = 'paymentTerm',
  FACTORING_FEE = 'factoringFee',
  FEE_DAYS_INCLUDED = 'daysIncluded',
  DAILY_FEE = 'dailyFee',
  MONTHLY_FEE = 'monthlyFee',
  ADMIN_FEE = 'administrationFee',
  TRANSFER_FEE = 'transferFee',
  NON_RECOURSE_FEE = 'nonRecourseFee',
  NON_RECOURSE_FEE_MINIMUM = 'nonRecourseFeeMinimum',
  CREDIT_LIMIT_FEE = 'creditLimitUpdateSettlementFee',
  ONBOARDING_FEE_TYPE = 'onboardingFeeType',
  ONBOARDING_FEE = 'onBoardingFee',
  ONBOARDING_FEE_PERCENTAGE = 'onboardingFeeInPercentage',
  ONBOARDING_FEE_CALCULATED = 'onboardingFeeInCalculated',
  RENEWAL_FEE_TYPE = 'renewalFeeType',
  RENEWAL_FEE = 'renewalFee',
  RENEWAL_FEE_PERCENTAGE = 'renewalFeeInPercentage',
  RENEWAL_FEE_CALCULATED = 'renewalFeeCalculated',
  FINANCIAL_LIMIT_INCREASE_FEE = 'financingIncreaseFee',
  FINANCIAL_LIMIT_INCREASE_FEE_MINIMUM = 'financingIncreaseFeeMinimum',
  VOLUME_COMMITMENT_PENALTY = 'penaltyOnVolume',
  GRACE_PERIOD = 'gracePeriod',
  INCREASED_FEE = 'increasedFee',
  EDIT_REASON = 'contractEditReason',
  ADD_SETTLEMENT = 'addSettlement',
  INSURED_RATE = 'insuredRate',
  INSURED_G_AMOUNT = 'getgAmountInsured',
  INSURANCE_TYPE = 'insuranceType',
  CLIENT_USES_G_AMOUNT = 'clientUsesGAmount',
  BENEFICIARY = 'beneficiary',
  IBAN = 'iban',
  BANK_ACCOUNT = 'bankAccount',
}

export interface ClientBeneficiaryDTO {
  beneficiary: string
  iban: string
}

export interface ContractsDetailsDTO {
  contractFeeDetails: ContractFeeDetailsDTO
  assignedDebtors: Array<ClientDebtorDTO>
  beneficiaries: Array<ClientBeneficiaryDTO>
  sourcingAgentAdministratesSpv: boolean
  sourcingAgentAdministrates: AdministrationTypes
  sourcingAgentCountry: CountryCodes
  clientId: string
  clientCompanyName: string
  clientUsesGAmount: boolean
}

export interface AddNewContractData {
  createContractDetails: ContractsDetailsDTO
}

export interface AddNewContractFormValues {
  sourcingAgentAdministrates: AdministrationTypes
  duration: number
  fundingLine: FundingLine | string
  clientId: string
  factoringProduct: FactoringProduct | string
  signingDate: string
  endDate: string
  contractTypeId: string
  currency: Currency | string
  contractName: string
}

export interface AddNewContractVariables extends AddNewContractFormValues {
  clientId: string
}

export interface DuplicateContractData {
  duplicateContract: ContractsDetailsDTO
}

export interface DuplicateContractVariables {
  contractId: string
  contractTypeId: string
  contractName: string
  sourcingAgentAdministrates: SourcingAgentTypes
  fundingLine: FundingLine
  signingDate: string
  endDate: string
  duration: number
}

export interface ClientContractFileDTO {
  content: string
  fileName: string
}

export interface GenerateContractDocumentData {
  generateContractDocument: ClientContractFileDTO
}

export interface GenerateContractDocumentVariables {
  contractId: string
  fileFormat?: FileFormat
}

export interface UpdateContractData {
  updateContractDetails: ContractsDetailsDTO
}

export interface UpdateContractVariables extends ContractFeeDetailsDTO {
  contractId: string
  addSettlement: boolean
}

export interface ClientContractData {
  contractDetails: ContractsDetailsDTO
}

export interface ClientContractVariables {
  contractId: string
}

export interface ClientDebtorCreditLimitDTO {
  availableCreditLimit: number
  creditLimit: number
  usedLimit: number
  __typename: string
}

export interface ClientDebtorCreditRiskDTO {
  debtorStatementCollected: boolean
  debtorStatementCollectedAt: Date
  debtorStatementCollectedBy: string
  externalTradingHistoryCollected: boolean
  externalTradingHistoryCollectedAt: Date
  externalTradingHistoryCollectedBy: string
  jointAssignmentCollected: boolean
  jointAssignmentCollectedAt: Date
  jointAssignmentCollectedBy: string
  tradingHistoryWithFactris: number
}

export enum CreditManagementPlanType {
  CUSTOM_INSURED = 'CUSTOM_INSURED',
  CUSTOM_UNINSURED = 'CUSTOM_UNINSURED',
  STANDARD_INSURED = 'STANDARD_INSURED',
  STANDARD_UNINSURED = 'STANDARD_UNINSURED',
}

export enum TaskAction {
  AUTOMATIC_TRANSFER_INVOICE_TO_INSURER = 'AUTOMATIC_TRANSFER_INVOICE_TO_INSURER',
  CALL_DEBTOR = 'CALL_DEBTOR',
  CHECK_IF_FEE_CALC_SHOULD_STOP = 'CHECK_IF_FEE_CALC_SHOULD_STOP',
  PREPARE_INVOICE_SUBMISSION_TO_INSURER = 'PREPARE_INVOICE_SUBMISSION_TO_INSURER',
  PURCHASE_STOP = 'PURCHASE_STOP',
  REMINDER_I = 'REMINDER_I',
  REMINDER_II = 'REMINDER_II',
  REMINDER_III = 'REMINDER_III',
  REQUEST_RECOURSE_TO_CLIENT = 'REQUEST_RECOURSE_TO_CLIENT',
  REVIEW = 'REVIEW',
}

export interface CreditManagementPlanActionDTO {
  action: TaskAction
  daysAfterDue: number
  __typename?: string
}

export interface CreditManagementPlanDTO {
  actions: Array<CreditManagementPlanActionDTO>
  cmpType: CreditManagementPlanType
}

export interface FinancingTermDTO {
  currency: Currency
  administrationFee: number
  administrationFeeOverrides: boolean
  contractId?: string
  contractName: string
  contractTypeName: string
  contractStatus: ContractStatus
  dailyFee: number
  dailyFeeOverrides: boolean
  daysIncluded: number
  daysIncludedOverrides: boolean
  factoringFee: number
  factoringFeeOverrides: boolean
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  gracePeriod: number
  gracePeriodOverrides: boolean
  increasedFee: number
  increasedFeeOverrides: boolean
  nonRecourseFee: number
  nonRecourseFeeOverrides: boolean
  paymentTerm: number
  paymentTermOverrides: boolean
  purchaseRate: number
  purchaseRateOverrides: boolean
  sourcingAgentAdministrates: AdministrationTypes
}

export enum ClientDebtorRelationshipStatus {
  APPROVED = 'APPROVED',
  ACTIVE = 'ACTIVE',
  RELATIONSHIP_CLOSED = 'CLOSED_RELATIONSHIP',
  INIT = 'INIT',
  REJECTED = 'REJECTED',
  ADD_DEBTOR_REQUESTED = 'ADD_DEBTOR_REQUESTED',
  NEW_CREDIT_LIMIT_REQUESTED = 'NEW_CREDIT_LIMIT_REQUESTED',
  CLOSE_RELATIONSHIP_REQUESTED = 'CLOSE_RELATIONSHIP_REQUESTED',
  PROCESSING_BY_CLIENT = 'PROCESSING_BY_CLIENT',
  NEEDS_CLARIFICATION = 'NEEDS_CLARIFICATION',
}

export enum ClientPortalClientDebtorRelationshipStatus {
  INIT = 'INIT',
  PROCESSING_BY_CLIENT = 'PROCESSING_BY_CLIENT',
  ADD_DEBTOR_REQUESTED = 'ADD_DEBTOR_REQUESTED',
  APPROVED = 'APPROVED',
  ACTIVE = 'ACTIVE',
  NEW_CREDIT_LIMIT_REQUESTED = 'NEW_CREDIT_LIMIT_REQUESTED',
  CLOSE_RELATIONSHIP_REQUESTED = 'CLOSE_RELATIONSHIP_REQUESTED',
  RELATIONSHIP_CLOSED = 'CLOSED_RELATIONSHIP',
  REJECTED = 'REJECTED',
}

export enum ClientDebtorRelationshipPendingStatus {
  PROCESSING_BY_CLIENT = 'PROCESSING_BY_CLIENT',
  ADD_DEBTOR_REQUESTED = 'ADD_DEBTOR_REQUESTED',
  NEW_CREDIT_LIMIT_REQUESTED = 'NEW_CREDIT_LIMIT_REQUESTED',
  CLOSE_RELATIONSHIP_REQUESTED = 'CLOSE_RELATIONSHIP_REQUESTED',
}

export enum ClientDebtorApprovalStatus {
  APPROVED = 'APPROVED',
  ACTIVE = 'ACTIVE',
  RELATIONSHIP_CLOSED = 'CLOSED_RELATIONSHIP',
  INIT = 'INIT',
  REJECTED = 'REJECTED',
  ADD_DEBTOR_REQUESTED = 'ADD_DEBTOR_REQUESTED',
  CLOSE_RELATIONSHIP_REQUESTED = 'CLOSE_RELATIONSHIP_REQUESTED',
  CLOSE_RELATIONSHIP_REJECTED = 'CLOSE_RELATIONSHIP_REJECTED',
  NEW_CREDIT_LIMIT_REQUESTED = 'NEW_CREDIT_LIMIT_REQUESTED',
  CREDIT_LIMIT_REJECTED = 'CREDIT_LIMIT_REJECTED',
  CREDIT_LIMIT_FULLY_APPROVED = 'CREDIT_LIMIT_FULLY_APPROVED',
  CREDIT_LIMIT_PARTLY_APPROVED = 'CREDIT_LIMIT_PARTLY_APPROVED',
  PROCESSING_BY_CLIENT = 'PROCESSING_BY_CLIENT',
}

export interface ClientDebtorApprovalDTO {
  approvalComment: string
  approvalExternalComment: string
  approvalStatus: ClientDebtorRelationshipStatus
  approver: string
  approverEmail: string
  decisionMadeOn: Date
}

export interface ApprovalChecklistDTO {
  allocatedCreditLimit: boolean
  setCreditManagementPlan: boolean
  setFinancingTerms: boolean
  verifyPrimaryBillingAddress: boolean
  setInvoiceVerificationConditions: boolean
  filledDebtorsRiskQuestionnaire: boolean
  debtorStatementUploaded: boolean
}

export enum CreditLimitRequestStatus {
  approved = 'approved',
  cancelled = 'cancelled',
  pending = 'pending',
  declined = 'declined',
}

export interface ClientRequestedCreditLimitDTO {
  currency: Currency
  approvedCreditLimit?: number
  approvedCreditLimitCurrency?: Currency
  clientComments: string
  createdOn?: Date
  decisionComment?: string
  amount: number
  monthlyTurnover: number
  paymentTerm: number
  status?: CreditLimitRequestStatus
}

export interface AddDebtorRequestDTO {
  addDebtorRequestComment: string
  addDebtorRequested: boolean
  addDebtorRequestedOn: Date
}

export interface RelationshipCloseRequestDTO {
  closeRequestComment: string
  closeRequestRejectComment: string
  closeRequestRejected: boolean
  closeRequestRejectedOn: Date
  closeRequested: boolean
  closeRequestedOn: Date
}

export interface DebtorRelationshipQuestionnaireDTO {
  newOrExistingDebtor: NewOrExistingType
  agreementDocumentName: string
  agreementDocumentType: AgreementDocumentType
  agreementDocumentId: string
  paymentHistoryDocumentName: string
  paymentHistoryDocumentId: string
  paymentHistoryDocumentType: PaymentHistoryDocumentType
  paymentHistoryExplanation: string
  dueDateOfOldestInvoice: Date
  notApplicableExplanation: string
}

export enum AuditActions {
  ADD_CLIENT_DEBTOR = 'ADD_CLIENT_DEBTOR',
  REQUESTED_DEBTOR_APPROVAL = 'REQUESTED_DEBTOR_APPROVAL',
  CLIENT_DEBTOR_CLOSE_RELATIONSHIP = 'CLIENT_DEBTOR_CLOSE_RELATIONSHIP',
  CLIENT_DEBTOR_REJECT_CLOSE_RELATIONSHIP = 'CLIENT_DEBTOR_REJECT_CLOSE_RELATIONSHIP',
  CLIENT_DEBTOR_APPROVE_RELATIONSHIP = 'CLIENT_DEBTOR_APPROVE_RELATIONSHIP',
  CLIENT_DEBTOR_ENABLE_RELATIONSHIP = 'CLIENT_DEBTOR_ENABLE_RELATIONSHIP',
  CLIENT_DEBTOR_REJECT_RELATIONSHIP = 'CLIENT_DEBTOR_REJECT_RELATIONSHIP',
  CLIENT_DEBTOR_INVOICE_VERIFICATION_CREATE = 'CLIENT_DEBTOR_INVOICE_VERIFICATION_CREATE',
  CLIENT_DEBTOR_INVOICE_VERIFICATION_UPDATE = 'CLIENT_DEBTOR_INVOICE_VERIFICATION_UPDATE',
  REQUESTED_CLOSE_DEBTOR_RELATIONSHIP = 'REQUESTED_CLOSE_DEBTOR_RELATIONSHIP',
  CANCEL_CLOSE_DEBTOR_RELATIONSHIP = 'CANCEL_CLOSE_DEBTOR_RELATIONSHIP',
  CLIENT_UPDATED_MODEL_AND_FEES = 'CLIENT_UPDATED_MODEL_AND_FEES',
  CLIENT_UPDATED_FINANCING_LIMIT = 'CLIENT_UPDATED_FINANCING_LIMIT',
  CLIENT_UPDATED_SALES_INFORMATION = 'CLIENT_UPDATED_SALES_INFORMATION',
  CLIENT_NAME_UPDATED = 'CLIENT_NAME_UPDATED',
  CLIENT_ADDED = 'CLIENT_ADDED',
  CLIENT_RISK_ALERTS_UPDATED = 'CLIENT_RISK_ALERTS_UPDATED',
  CLIENT_UPDATED_COMPANY_DETAILS = 'CLIENT_UPDATED_COMPANY_DETAILS',
  CLIENT_UPDATED_COMMUNICATION_PREF = 'CLIENT_UPDATED_COMMUNICATION_PREF',
  CLIENT_FACTORING_MODELS_UPDATE = 'CLIENT_FACTORING_MODELS_UPDATE',
  CLIENT_UPDATED_DETAILS = 'CLIENT_UPDATED_DETAILS',
  OUTSTANDING_VERIFICATION_APPROVED = 'OUTSTANDING_VERIFICATION_APPROVED',
  OUTSTANDING_VERIFICATION_ARCHIVED = 'OUTSTANDING_VERIFICATION_ARCHIVED',
  OUTSTANDING_VERIFICATION_CREATED = 'OUTSTANDING_VERIFICATION_CREATED',
  OUTSTANDING_VERIFICATION_DEBTOR_APPROVED = 'OUTSTANDING_VERIFICATION_DEBTOR_APPROVED',
  OUTSTANDING_VERIFICATION_DEBTOR_REJECTED = 'OUTSTANDING_VERIFICATION_DEBTOR_REJECTED',
  OUTSTANDING_VERIFICATION_REJECTED = 'OUTSTANDING_VERIFICATION_REJECTED',
  OUTSTANDING_VERIFICATION_RESPONDED = 'OUTSTANDING_VERIFICATION_RESPONDED',
  OUTSTANDING_VERIFICATION_SENT_TO_DEBTOR = 'OUTSTANDING_VERIFICATION_SENT_TO_DEBTOR',
  OUTSTANDING_VERIFICATION_STARTED_INVESTIGATION = 'OUTSTANDING_VERIFICATION_STARTED_INVESTIGATION',
  OUTSTANDING_VERIFICATION_DEBTOR_APPROVED_FROM_EMAIL = 'OUTSTANDING_VERIFICATION_DEBTOR_APPROVED_FROM_EMAIL ',
  RISK_OFFER_DETAILS_UPDATED = 'RISK_OFFER_DETAILS_UPDATED',
  UPDATED_CLIENT_DEBTOR_STATEMENT = 'UPDATED_CLIENT_DEBTOR_STATEMENT',
  UPLOADED_CLIENT_DEBTOR_STATEMENT = 'UPLOADED_CLIENT_DEBTOR_STATEMENT',
  SEND_CLIENT_DEBTOR_STATEMENT = 'SEND_CLIENT_DEBTOR_STATEMENT',
  PROSPECT_DEBTOR_NAME_UPDATED = 'PROSPECT_DEBTOR_NAME_UPDATED',
  CLIENT_AML_RISK_UPDATED = 'CLIENT_AML_RISK_UPDATED',
}

export interface ClientDebtorAuditDTO {
  action: AuditActions
  createdAt: Date
  externalComment: string
  id: string
  internalComment: string
  userFullName: string
  emails: string[]
  debtorStatementRequired: boolean
  debtorStatementType: DebtorStatementType
  copiedFromId: string
  copiedFromName: string
}

export interface DebtorRiskQuestionnaireHitDTO {
  companyHitInOndato: boolean
  concentrationBreachPerFundingLine: boolean
  hitCountrySPRatingHigh: boolean
  hitCurrentRatioLessEqualOne: boolean
  hitEquityLessFifteen: boolean
  hitInMarketLessOneYear: boolean
  hitInStopList: boolean
  hitProfitabilityLessZero: boolean
  notInOECDList: boolean
  notReceivingInsurance: boolean
  personHitInOndato: boolean
  hitNegativeOrZeroEquity: boolean
  hitNoCreditSafeLimit: boolean
  hitCompanyStatusNotActive: boolean
  hitNoRecentPublishedAccounts: boolean
  hitRecentNegativeInformation: boolean
  hitPaymentExperienceViaFactris: boolean
  hitCompanyChangesInSixMonths: boolean
  hitBranchOfForeignCompany: boolean
}

export interface ClientDebtorRiskAlertsDTO {
  blockCnPayments: boolean
  blockPnPayments: boolean
  peppolDisabledForDebtor: boolean
  autoFundingDisable: boolean
}

export interface DebtorStatementRequirementDTO {
  debtorStatementRequired: boolean
  debtorStatementType: DebtorStatementType
}

export interface DebtorStatementSummaryDTO {
  id: string
  agreementNumber: string
  attachedDocument: ReportFileDTOInput
  attachedDocumentId: string
  attachedDocumentName: string
  attachedDocumentUploadedAt: Date
  attachedDocumentUploader: string
  debtorStatementNumber: string
  debtorStatementType: DebtorStatementType
  documentLanguage: string
  invoices: Array<DebtorStatementInvoice>
  lastTimeSentAt: Date
  lastTimeSentBy: string
  lastTimeSentTo: string[]
  setOffRate: number
  startDate: string
  city: string
  country: string
  postalCode: string
  registrationNumber: string
  streetAddressLine1: string
  streetAddressLine2: string
  vatNumber: string
  debtorName: string
  gAmountApplied: boolean
  gAmount: number
}

export enum DebtorStatementFormType {
  EDIT = 'EDIT',
  SEND_DOCUMENT = 'SEND_DOCUMENT',
  UPLOAD_SIGNED_DOCUMENT = 'UPLOAD_SIGNED_DOCUMENT',
}

export interface ClientDebtorDetailsDTO {
  companyName: string
  clientId: string
  debtorId: string
  clientCompanyName: string
  creditLimitFee: number
  addedByClient: boolean
  peppolSendEnabled: boolean
  clientAutoFundingEnabled: boolean
  dunsDetails: DebtorDunsDetailsDTO
  details: DebtorDetailsInfoDTO
  financingTerm: FinancingTermDTO
  creditLimit: ClientDebtorCreditLimitDTO
  creditLimitPLN: ClientDebtorCreditLimitDTO
  requestedCreditLimit: ClientRequestedCreditLimitDTO
  relationshipCloseRequest: RelationshipCloseRequestDTO
  addDebtorRequest: AddDebtorRequestDTO
  relationshipQuestionnaire: DebtorRelationshipQuestionnaireDTO
  creditRisk: ClientDebtorCreditRiskDTO
  creditManagementPlan: CreditManagementPlanDTO
  clientSourcingAgentName: string
  clientSourcingAgentAdministrates: string
  approval: ClientDebtorApprovalDTO
  approvalChecklist: ApprovalChecklistDTO
  status: ClientDebtorStatus
  blockInfo: DebtorBlockInfoDTO
  clientDebtorLogs: Array<ClientDebtorAuditDTO>
  riskQuestionnaireHits: DebtorRiskQuestionnaireHitDTO
  riskAlerts: ClientDebtorRiskAlertsDTO
  debtorStatementRequirement: DebtorStatementRequirementDTO
  debtorStatementSummary: DebtorStatementSummaryDTO
}

export interface ClientDebtorDetailsData {
  clientDebtorDetails: ClientDebtorDetailsDTO
}

export interface ClientDebtorDetailsVariables {
  clientDebtorId: string
}

export interface AddNewClientDebtorData {
  addClientDebtor: ClientDebtorDetailsDTO
}

export interface AddNewClientDebtorVariables {
  clientId: string
  dunId: string
}

export interface DownloadDebtorBulkImportTemplateData {
  downloadDebtorsImportTemplate: DownloadableFileDTO
}

export interface DownloadDebtorBulkImportTemplateVariables {
  language: string
}

export interface UploadedDebtorDTO {
  id: string
  addressCity: string
  addressCountry: string
  addressPostalCode: string
  addressStreet1: string
  addressStreet2: string
  comment: string
  companyCountryCode: string
  companyName: string
  contactName: string
  contactPhoneNumber: string
  dunId: string
  overdueEmail: string
  preferredLanguage: string
  registrationNumber: string
  vat: string
  verificationEmail: string
}

export interface DebtorUploadResultDTO {
  clientId?: string
  corruptedDebtors: UploadedDebtorDTO[]
  failedImports: number
  successfulImports: number
}

export interface AddMultipleDebtorsData {
  uploadDebtorsFromFile: DebtorUploadResultDTO
}

export interface AddMultipleDebtorsVariables {
  clientId: string
  file: DownloadableFileDTO
}

export interface AddMultipleDebtorsFormValues {
  file: DownloadableFileDTO
}

export enum AddMultipleIDebtorsFieldsNames {
  FILE = 'file',
}

export interface ReUploadDebtorData {
  reUploadDebtor: DebtorUploadResultDTO
}

export interface UploadedDebtorDTOInput {
  searchType?: DUNSSearchType
  addressCity: string
  addressCountry: string
  addressPostalCode: string
  addressStreet1: string
  addressStreet2: string
  comment: string
  companyCountryCode?: string
  dunId: string
  companyName?: string
  contactName: string
  contactPhoneNumber: string
  overdueEmail: string
  preferredLanguage: string
  registrationNumber?: string
  vat?: string
  verificationEmail: string
}

export interface ReUploadDebtorVariables {
  clientId: string
  correctedCorruptedDebtor: UploadedDebtorDTOInput
}

export enum ReUploadDebtorFieldsNames {
  DUN_ID = 'dunId',
  SEARCH_TYPE = 'searchType',
  COUNTRY_CODE = 'companyCountryCode',
  COMPANY_NAME = 'companyName',
  REGISTRATION_NUMBER = 'registrationNumber',
  VAT = 'vat',
  COMMENT = 'comment',
  CONTACT_NAME = 'contactName',
  CONTACT_PHONE = 'contactPhoneNumber',
  PREFERRED_LANGUAGE = 'preferredLanguage',
  STREET_1 = 'addressStreet1',
  STREET_2 = 'addressStreet2',
  CITY = 'addressCity',
  POSTAL_CODE = 'addressPostalCode',
  COUNTRY = 'addressCountry',
  VERIFICATION_EMAIL = 'verificationEmail',
  OVERDUE_EMAIL = 'overdueEmail',
}

export interface AvailableForContractDebtorsData {
  contractAvailableDebtors: Array<ClientDebtorDTO>
}

export interface AvailableForContractDebtorsVariables {
  contractId: string
}

export interface AddDebtorToContractData {
  addDebtorToContract: ContractsDetailsDTO
}

export interface AddDebtorToContractFormValues {
  clientDebtorIds: Array<string>
}

export enum AddDebtorToContractFieldsNames {
  CLIENT_DEBTORS_IDS = 'clientDebtorIds',
}

export interface AddDebtorToContractVariables extends AddDebtorToContractFormValues {
  contractId: string
}

export interface SwitchDebtorToContractData {
  switchDebtorToContract: ContractsDetailsDTO
}

export interface SwitchDebtorToContractFormValues {
  contractToId: string
}

export enum SwitchDebtorToContractFieldsNames {
  CONTRACT_TO_ID = 'contractToId',
}

export interface SwitchDebtorToContractVariables extends SwitchDebtorToContractFormValues {
  clientDebtorIds: Array<string>
  contractFromId: string
}

export interface UpdateClientDebtorRiskAlertsData {
  updateClientDebtorRiskAlerts: ClientDebtorDetailsDTO
}

export interface UpdateClientDebtorRiskAlertsVariables {
  clientDebtorId: string
  blockCnPayments: boolean
  blockPnPayments: boolean
  peppolDisabledForDebtor: boolean
  autoFundingDisable: boolean
}

export interface UpdateClientDebtorFinancingTermsData {
  updateClientDebtorFinancingTerm: ClientDebtorDetailsDTO
}

export interface UpdateClientDebtorFinancingTermsFormValues {
  currency: Currency
  administrationFee: number
  administrationFeeOverrides?: boolean
  contractId?: string
  contractTypeName: string
  dailyFee: number
  dailyFeeOverrides?: boolean
  daysIncluded: number
  daysIncludedOverrides?: boolean
  factoringFee: number
  factoringFeeOverrides?: boolean
  factoringProduct: FactoringProduct
  fundingLine: FundingLine
  gracePeriod: number
  gracePeriodOverrides?: boolean
  increasedFee: number
  increasedFeeOverrides?: boolean
  nonRecourseFee: number
  nonRecourseFeeOverrides?: boolean
  paymentTerm: number
  paymentTermOverrides?: boolean
  contractStatus?: ContractStatus
  contractTypeId?: string
  duration?: number
  id?: string
  signingDate?: Date
  override?: boolean
  purchaseRate?: number
}

export interface UpdateClientDebtorFinancingTermsVariables extends UpdateClientDebtorFinancingTermsFormValues {
  clientDebtorId: string
}

export interface EditClientDebtorCreditRiskData {
  editClientDebtorCreditRisk: ClientDebtorDetailsDTO
}

export interface EditClientDebtorCreditRiskVariables extends EditClientDebtorCreditRiskFormValues {
  clientDebtorId: string
}

export interface EditClientDebtorCreditRiskFormValues {
  debtorStatementCollected: boolean
  externalTradingHistoryCollected: boolean
}

export enum ClientDebtorCreditRiskFieldNames {
  DEBTOR_STATEMENT_COLLECTED = 'debtorStatementCollected',
  TRADING_HISTORY_COLLECTED = 'externalTradingHistoryCollected',
  JOINT_ASSIGNMENT_COLLECTED = 'jointAssignmentCollected',
}

export enum ContractFactoringProduct {
  AMERICAN_FACTORING_RECOURSE = 'AMERICAN_FACTORING_RECOURSE',
  AMERICAN_FACTORING_NON_RECOURSE = 'AMERICAN_FACTORING_NON_RECOURSE',
  INVOICE_FINANCING_RECOURSE = 'INVOICE_FINANCING_RECOURSE',
  INVOICE_FINANCING_NON_RECOURSE = 'INVOICE_FINANCING_NON_RECOURSE',
  INVOICE_DISCOUNTING_NON_RECOURSE = 'INVOICE_DISCOUNTING_NON_RECOURSE',
  AMERICAN_FACTORING_MIXED = 'AMERICAN_FACTORING_MIXED',
}

export interface ContractTypeDTO {
  active: boolean
  contractType: string
  country: string
  factoringProducts: Array<FactoringProduct>
  fundingLineTypes: Array<FundingLine>
  currencies: Array<Currency>
  id: string
}

export interface ClientContractTypesData {
  contractTypeList: Array<ContractTypeDTO>
}

export interface ClientContractTypesVariables {
  clientId?: string
  sourcingAgentAdministrates?: SourcingAgentTypes
}

export interface AccountManagerDTO {
  fullName: string
  usersLogin: string
}

export interface AccountManagersData {
  accountManagersWithEmails: Array<AccountManagerDTO>
}

export interface AccountManagersVariables {
  name: string
}

export interface ClientBrokerCommissionDTO {
  brokerCommission: number
  brokerName: string
  id: string
}

export interface ClientBrokersData {
  filterBrokerCommissions: Array<ClientBrokerCommissionDTO>
}

export interface ClientBrokersVariables {
  brokerName: string
}

export enum SettlementStatus {
  NOT_PAID = 'NOT_PAID',
  ON_HOLD = 'ON_HOLD',
  PAID = 'PAID',
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  INVOICED = 'INVOICED',
  DELETED = 'DELETED',
}

export enum SettlementFeeType {
  ADMINISTRATION_FEE = 'ADMINISTRATION_FEE',
  COMPENSATION_INDEMNITY_FEE = 'COMPENSATION_INDEMNITY_FEE',
  CREDIT_LIMIT_FEE = 'CREDIT_LIMIT_FEE',
  ONBOARDING_FEE = 'ONBOARDING_FEE',
  INVOICE_SETTLEMENT = 'INVOICE_SETTLEMENT',
}

export enum LoanBookSettlementFeeTypeList {
  ADMINISTRATION_FEE = 'ADMINISTRATION_FEE',
  COMPENSATION_INDEMNITY_FEE = 'COMPENSATION_INDEMNITY_FEE',
  LIMIT_INCREASE_FEE = 'LIMIT_INCREASE_FEE',
  ONBOARDING_FEE = 'ONBOARDING_FEE',
}

export enum FactoringSettlementFeeTypeList {
  ADMINISTRATION_FEE = 'ADMINISTRATION_FEE',
  COMPENSATION_INDEMNITY_FEE = 'COMPENSATION_INDEMNITY_FEE',
  CREDIT_LIMIT_FEE = 'CREDIT_LIMIT_FEE',
  ONBOARDING_FEE = 'ONBOARDING_FEE',
}

export interface SettlementDTO {
  amount: number
  openAmount: number
  usedAmount: number
  vat: number
  createdBy: string
  createdOn: Date
  description: string
  id: string
  status: SettlementStatus
  feeType: SettlementFeeType
  actions?: ReactNode
  dilutionType: SettlementDilutionType
  clientCreditNoteId: string
  invoiceId: string
  invoiceNumber: string
  clientCreditNoteNumber: string
  debtorCompanyName: string
  clientDebtorId: string
  currency: Currency
  productType: SettlementProductType
  sourcingAgentAdministrates: AdministrationTypes
  rpaNumber: string
  contractName: string
  contractId: string
  contractGroupId: string
}

export interface Page_SettlementDTO {
  content: Array<SettlementDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClienSettlementsData {
  clientSettlementList: Page_SettlementDTO
}

export interface ClientSettlementsVariables {
  clientId: string
  duration?: number
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export enum SettlementProductType {
  FACTORING = 'FACTORING',
  LOAN_BOOK = 'LOAN_BOOK',
}

export interface CreateClientSettlementData {
  createSettlement: SettlementDTO
}

export interface CreateClientSettlementVariables {
  clientId: string
  amount: number
  clientDebtorId: string
  vat: number
  description: string
  feeType: SettlementFeeType
  setOnHold: boolean
  currency: Currency
  productType: SettlementProductType
  issueVatInvoice: boolean
  contractId?: string
  loanBookAgreementId?: string
}

export interface CreateClientSettlementFormData {
  hasClientDebtor?: boolean
  amount: number
  clientDebtorId: string
  vat: number
  description: string
  feeType: SettlementFeeType
  setOnHold: boolean
  currency: Currency
  productType: SettlementProductType
  loanBookAgreementId: string
  contractId: string
  issueVatInvoice: boolean
}

export enum ClientSettlementFieldNames {
  AMOUNT = 'amount',
  HAS_CLIENT_DEBTOR = 'hasClientDebtor',
  CLIENT_DEBTOR_ID = 'clientDebtorId',
  VAT = 'vat',
  DESCRIPTION = 'description',
  TYPE = 'feeType',
  SET_ON_HOLD = 'setOnHold',
  CURRENCY = 'currency',
  PRODUCT_TYPE = 'productType',
  AGREEMENT_ID = 'loanBookAgreementId',
  CONTRACT_ID = 'contractId',
  ISSUE_VAT_INVOICE = 'issueVatInvoice',
}

export interface DeleteClientSettlementData {
  deleteSettlement: SettlementDTO
}

export interface DeleteClientSettlementVariables {
  settlementId: string
}

export interface OffHoldClientSettlementData {
  offHoldSettlement: SettlementDTO
}

export interface OffHoldClientSettlementVariables {
  settlementId: string
}

export interface SetHoldClientSettlementData {
  setSettlementToHold: SettlementDTO
}

export interface SetHoldClientSettlementVariables {
  settlementId: string
}

export interface GetClientSettlementVatData {
  settlementVats: Array<number>
}

export interface IssueSettlementVatInvoiceVariables {
  settlementId: string
}

export interface IssueSettlementVatInvoiceData {
  issueSettlementVatInvoice: string
}

export interface GetClientSettlementVatVariables {
  clientId: string
  feeType: SettlementFeeType
  contractId?: string
}

export interface CreditManagementPlanPlansData {
  getPlanFromClassifier: CreditManagementPlanDTO
}

export interface CreditManagementPlanPlansVariables {
  cmpType: CreditManagementPlanType
}

export interface AssignCmpPlanToClientDebtorData {
  assignCreditManagementPlanToClientDebtor: CreditManagementPlanDTO
}

export interface AssignCmpPlanFormValues {
  cmpType: CreditManagementPlanType
  actions: Array<CreditManagementPlanActionDTO>
}

export interface AssignCmpPlanToClientDebtorVariables extends AssignCmpPlanFormValues {
  clientDebtorId: string
}

export enum AssignCmpPlanFieldsNames {
  TYPE = 'cmpType',
  ACTIONS = 'actions',
}

export interface ClientCommentDTO {
  archived: boolean
  clientId: string
  comment: string
  createdBy: string
  createdByEmail: string
  createdById: string
  createdOn: Date
  legacy: boolean
  flagged: boolean
  id: string
  updatedBy: string
  updatedByEmail: string
  updatedById: string
  updatedOn: Date
}

export interface GetClientCommentsData {
  getClientComments: Array<ClientCommentDTO>
}

export interface GetClientCommentsVariables {
  clientId: string
}

export interface GetClientCommentByIdData {
  getClientCommentById: ClientCommentDTO
}

export interface GetClientCommentsByIdVariables {
  commentId: string
}

export interface AddClientCommentData {
  addClientComment: ClientCommentDTO
}

export interface AddClientCommentVariables {
  clientId: string
  comment: string
}

export interface UpdateClientCommentData {
  updateClientComment: ClientCommentDTO
}

export interface UpdateClientCommentVariables {
  comment: string
  commentId: string
}

export interface MarkClientCommentAsFlaggedData {
  markClientCommentAsFlagged: ClientCommentDTO
}

export interface MarkClientCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkClientCommentAsUnflaggedData {
  markClientCommentAsUnflagged: ClientCommentDTO
}

export interface MarkClientCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkClientCommentAsArchivedData {
  markClientCommentAsArchived: ClientCommentDTO
}

export interface MarkClientCommentAsArchivedVariables {
  commentId: string
}

export interface MarkClientCommentAsUnarchivedData {
  markClientCommentAsUnarchived: ClientCommentDTO
}

export interface MarkClientCommentAsUnarchivedVariables {
  commentId: string
}

export enum CommentFieldNames {
  COMMENT = 'comment',
}

export enum ClientDebtorCollateralType {
  ALLIANZ = 'ALLIANZ',
  ATRADIUS = 'ATRADIUS',
  CAP_R = 'CAP_R',
  CREDITSAFE = 'CREDITSAFE',
  INVEGA = 'INVEGA',
  NON_INSURED = 'NON_INSURED',
  PAYMENT_HISTORY = 'PAYMENT_HISTORY',
}

export interface ClientDebtorCollateralDTO {
  amount: number
  expiry: Date
  collateralType: ClientDebtorCollateralType
  currency: Currency
}

export enum CollateralLogEvent {
  NEW_LIMIT_REQUESTED = 'NEW_LIMIT_REQUESTED', // In Client portal
  COLLATERALS_UPDATED = 'COLLATERALS_UPDATED',
  REJECT_LIMIT_REQUEST = 'REJECT_LIMIT_REQUEST',
  APPROVE_LIMIT_REQUEST = 'APPROVE_LIMIT_REQUEST',
  CANCEL_LIMIT_REQUEST = 'CANCEL_LIMIT_REQUEST', // In Client portal
}

export interface ClientDebtorCollateralLogDTO {
  clientDebtorId: string
  comments: string
  creditLimitAmount: number
  creditLimitAmountCurrency: Currency
  approvedAmount: number
  approvedAmountCurrency: Currency
  event: CollateralLogEvent
  monthlyTurnover: number
  paymentTerm: number
  updatedAt: Date
  updatedByEmail: string
  updatedByFullName: string
  updatedById: string
  collaterals: Array<ClientDebtorCollateralDTO>
}

export interface LimitDistributionDTO {
  guarantee: number
  insured: number
  nonRecourseLimit: number
  recourseLimit: number
  totalCreditLimit: number
  uninsured: number
  currency: Currency
}

export interface ClientDebtorCollateralDetailsDTO {
  hasAegonContract: boolean
  caprLimitFeePercent: number
  creditLimitFee: number
  currency: Currency
  collaterals: Array<ClientDebtorCollateralDTO>
  settlements: Array<SettlementDTO>
  limitDistribution: LimitDistributionDTO
  limitDistributionPLN: LimitDistributionDTO
  collateralLogs: Array<ClientDebtorCollateralLogDTO>
}

export interface AssuranceFormData {
  caprLimitFeePercent: number
  collaterals: Array<ClientDebtorCollateralDTO>
  creditLimitFee: number
}

export interface LimitsDistributionCardData {
  guarantee: number
  insured: number
  nonRecourseLimit: number
  recourseLimit: number
  totalCreditLimit: number
  uninsured: number
}

export interface ClientDebtorCollateralsData {
  clientDebtorCollaterals: ClientDebtorCollateralDetailsDTO
}

export interface DebtorCollateralsVariables {
  clientDebtorId: string
}

export interface UpdateDebtorCollateralsData {
  updateClientDebtorCollaterals: ClientDebtorCollateralDetailsDTO
}

export interface ClientDebtorCollateralDTOInput {
  amount: number
  collateralType: ClientDebtorCollateralType
  expiry: Date
  currency: Currency
}

export interface UpdateDebtorCollateralsVariables {
  caprLimitFee: number
  creditLimitFee: number
  caprLimitFeePLN: number
  collaterals: Array<ClientDebtorCollateralDTOInput>
  clientDebtorId: string
}

export interface UpdateDebtorAssuranceFormValues {
  collaterals: Array<ClientDebtorCollateralDTOInput>

  canAddCreditLimitFee?: boolean
  creditLimitFeeType?: FeeType
  creditLimitFee?: number

  caprLimitFeePercent?: number

  canAddCaprFee?: boolean
  caprLimitFeeType?: FeeType
  caprLimitFee?: number

  canAddCaprFeePLN?: boolean
  caprLimitFeeTypePLN?: FeeType
  caprLimitFeePLN?: number
}

export enum FeeType {
  CALCULATED = 'CALCULATED',
  CUSTOM = 'CUSTOM',
  FREE = 'FREE',
}

export enum CollateralsFieldNames {
  COLLATERALS = 'collaterals',
  CONTRACT_CURRENCY = 'contractCurrency',
  HAS_AEGON_CONTRACT = 'hasAegonContract',
  CAN_EDIT_CREDIT_LIMIT_FEE = 'canAddCreditLimitFee',
  CREDIT_LIMIT_FEE_TYPE = 'creditLimitFeeType',
  CREDIT_LIMIT_FEE = 'creditLimitFee',
  CAP_R_FEE_PERCENT = 'caprLimitFeePercent',
  CAN_EDIT_CAP_R_FEE = 'canAddCaprFee',
  CAP_R_FEE_TYPE = 'caprLimitFeeType',
  CAP_R_FEE = 'caprLimitFee',
  CAN_EDIT_CAP_R_FEE_PLN = 'canAddCaprFeePLN',
  CAP_R_FEE_TYPE_PLN = 'caprLimitFeeTypePLN',
  CAP_R_FEE_PLN = 'caprLimitFeePLN',
}

export enum CollateralsFieldsArrayNames {
  AMOUNT = 'amount',
  CURRENCY = 'currency',
  TYPE = 'collateralType',
  EXPIRY = 'expiry',
}

export interface CollateralCheckDTO {
  canAddCaprFee: boolean
  canAddCaprFeePLN: boolean
  canAddCreditLimitFee: boolean
}

export interface CheckDebtorCollateralsData {
  checkClientDebtorCollaterals: CollateralCheckDTO
}

export interface CheckDebtorCollateralsVariables {
  collaterals: Array<ClientDebtorCollateralDTOInput>
  clientDebtorId: string
}

export enum FinancingTermsFieldNames {
  CONTRACT = 'contract',
  OVERRIDE = 'override',
  CONTRACT_ID = 'contractId',
  PAYMENT_TERM = 'paymentTerm',
  FACTORING_FEE = 'factoringFee',
  FEE_DAYS_INCLUDED = 'daysIncluded',
  DAILY_FEE = 'dailyFee',
  ADMIN_FEE = 'administrationFee',
  NON_RECOURSE_FEE = 'nonRecourseFee',
  GRACE_PERIOD = 'gracePeriod',
  INCREASED_FEE = 'increasedFee',
  ADVANCED_RATE = 'purchaseRate',
}

export interface UpdateClientDebtorDunsDetailsData {
  updateClientDebtorDunsDetails: DebtorDunsDetailsDTO
}

export interface UpdateClientDebtorDunsDetailsVariables {
  clientDebtorId: string
}

export interface ApproveClientDebtorRelationshipData {
  approveDebtorRelationship: ClientDebtorDetailsDTO
}

export interface ApproveClientDebtorRelationshipVariables extends ClientDebtorRelationshipApprovalFormValues {
  clientDebtorId: string
}

export interface RejectClientDebtorRelationshipData {
  rejectDebtorRelationship: ClientDebtorDetailsDTO
}

export interface RejectClientDebtorRelationshipVariables extends ClientDebtorRelationshipApprovalFormValues {
  clientDebtorId: string
}

export interface CloseClientDebtorRelationshipData {
  closeDebtorRelationship: ClientDebtorDetailsDTO
}

export interface CloseClientDebtorRelationshipVariables extends ClientDebtorRelationshipApprovalFormValues {
  clientDebtorId: string
}

export interface CloseDebtorRelationshipBulkData {
  closeDebtorRelationshipBulk: boolean
}

export interface CloseDebtorRelationshipBulkVariables {
  clientDebtorIds: Array<string>
  externalComments?: string
  comments: string
}

export interface EnableClientDebtorRelationshipData {
  enableDebtorRelationship: ClientDebtorDetailsDTO
}

export interface EnableClientDebtorRelationshipVariables {
  clientDebtorId: string
}

export interface NotifyOpsForApprovalData {
  notifyOpsForApproval: ClientDebtorDetailsDTO
}

export interface NotifyOpsForApprovalVariables {
  clientDebtorId: string
}

export interface PokeClientData {
  sendPendingNewDebtorAddInfoReminder: boolean
}

export interface PokeClientVariables {
  clientDebtorId: string
}

export interface RejectClientDebtorCloseRelationshipRequestData {
  closeDebtorRelationshipRejected: ClientDebtorDetailsDTO
}

export interface RejectClientDebtorCloseRelationshipRequestFormValues {
  comments: string
}

export interface RejectClientDebtorCloseRelationshipRequestVariables extends RejectClientDebtorCloseRelationshipRequestFormValues {
  clientDebtorId: string
}

export interface ClientDebtorRelationshipApprovalFormValues {
  externalComments?: string
  comments: string
}

export enum ClientDebtorRelationshipApprovalFieldNames {
  INTERNAL_COMMENTS = 'comments',
  EXTERNAL_COMMENTS = 'externalComments',
}

export interface ClientDebtorRequestClarificationData {
  startClientDebtorClarification: ClientDebtorDetailsDTO
}

export interface ClientDebtorRequestClarificationVariables extends ClientDebtorRequestClarificationFormValues {
  clientDebtorId: string
}

export enum TeamRole {
  CREDIT_COMMITTEE = 'CREDIT_COMMITTEE',
  DEBT_COLLECTION = 'DEBT_COLLECTION',
  OPERATIONS = 'OPERATIONS',
  SALES = 'SALES',
}

export interface ClientDebtorRequestClarificationFormValues {
  comment: string
  questionToTeam?: TeamRole
}

export enum ClientDebtorRequestClarificationFieldNames {
  COMMENT = 'comment',
  QUESTION_TO_TEAM = 'questionToTeam',
}

export interface ClientDebtorFinishClarificationData {
  finishClientDebtorClarification: ClientDebtorDetailsDTO
}

export interface ClientDebtorFinishClarificationVariables {
  clientDebtorId: string
  clarificationId?: string
}

export interface ClientDebtorClarificationDTO {
  closed: boolean
  comment: string
  createdOn: string
  explanations: Array<ClarificationCommentDTO>
  fullName: string
  usersLogin: string
}

export interface ClientDebtorClarificationsData {
  clientDebtorClarifications: Array<ClientDebtorClarificationDTO>
}

export interface ClientDebtorClarificationsVariables {
  clientDebtorId: string
}

export interface AddClientDebtorClarificationCommentData {
  addClientDebtorClarificationComment: Array<ClientDebtorClarificationDTO>
}

export interface AddClientDebtorClarificationCommentVariables {
  clientDebtorId: string
  clarificationId?: string
  comment: string
}

export interface ApproveNewClientDebtorCreditLimitData {
  approveCreditLimit: ClientDebtorDetailsDTO
}

export interface ApproveNewClientDebtorCreditLimitVariables {
  clientDebtorId: string
}

export interface RejectNewClientDebtorCreditLimitData {
  rejectCreditLimit: ClientDebtorDetailsDTO
}

export interface RejectNewClientDebtorCreditLimitFormValues {
  comments: string
  creditLimitFee: number
  creditLimitFeeType: FeeType
}

export enum RejectNewClientDebtorCreditLimitFieldsNames {
  COMMENTS = 'comments',
  CREDIT_LIMIT_FEE = 'creditLimitFee',
  CREDIT_LIMIT_FEE_TYPE = 'creditLimitFeeType',
}

export interface RejectNewClientDebtorCreditLimitVariables extends RejectNewClientDebtorCreditLimitFormValues {
  clientDebtorId: string
}

export interface DebtorAddressDTO {
  city: string
  country: string
  postalCode: string
  street1: string
  street2: string
}

export interface DebtorOverdueEmailDTO {
  email: string
  useForNotifications: boolean
  [field: string]: any
}

export interface DebtorPhoneDTO {
  phoneNumber: string
  name: string

  [field: string]: string | boolean | Date
}

export interface DebtorVerificationEmailDTO {
  email: string
  preferredLanguage: string
  verificationComment?: string
  useForNotifications: boolean
  verified?: boolean
  verifiedBy?: string
  verifiedOn?: Date | string
  additionalFiles?: ReportFileDTOInput[]
  __typename?: string
  [key: string]: any
}

export interface DebtorContactDTO {
  id: string
  deleted?: boolean
  address: DebtorAddressDTO
  overdueEmails: Array<DebtorOverdueEmailDTO>
  phones: Array<DebtorPhoneDTO>
  verificationEmails: Array<DebtorVerificationEmailDTO>
}

export interface ClientDebtorContactLogDTO {
  action: AuditActions
  createdAt: Date
  id: string
  updatedByFullName: string
  userLogin: string
  verificationEmails: string
  copiedFromId: string
  copiedFromName: string
}

export interface ClientDebtorContactLogsResponseDTO {
  contacts: Array<DebtorContactDTO>
  logs: Array<ClientDebtorContactLogDTO>
}

export interface ClientDebtorBillingAddressesData {
  getDebtorContacts: Array<DebtorContactDTO>
}

export interface ClientDebtorBillingAddressesVariables {
  clientDebtorId: string
}

export interface ClientDebtorBillingAddressesWithLogsData {
  getDebtorContactsLogs: ClientDebtorContactLogsResponseDTO
}

export interface ClientDebtorBillingAddressesWithLogsVariables {
  clientDebtorId: string
}

export interface AddClientDebtorBillingAddressesData {
  addDebtorContact: DebtorContactDTO
}

export enum ClientDebtorBillingAddressesFieldsNames {
  ID = 'id',
  STREET_1 = 'street1',
  STREET_2 = 'street2',
  CITY = 'city',
  POSTAL_CODE = 'postalCode',
  COUNTRY = 'country',
  VERIFICATION_EMAILS = 'verificationEmails',
  OVERDUE_EMAILS_TYPE = 'overdueEmailsType',
  OVERDUE_EMAILS = 'overdueEmails',
  PHONES = 'phones',
}

export enum ClientDebtorPhonesFieldArrayNames {
  PHONE = 'phoneNumber',
  NAME = 'name',
}

export enum ClientDebtorVerificationEmailsFieldArrayNames {
  EMAIL = 'email',
  LANGUAGE = 'preferredLanguage',
  COMMENT = 'verificationComment',
  VERIFIED = 'verified',
  VERIFIED_BY = 'verifiedBy',
  VERIFIED_BY_EMAIL = 'verifiedByEmail',
  VERIFIED_BY_ID = 'verifiedById',
  VERIFIED_ON = 'verifiedOn',
  USE_FOR_NOTIFICATIONS = 'useForNotifications',
  ADDITIONAL_FILES = 'additionalFiles',
}

export enum ClientDebtorOverdueEmailsFieldArrayNames {
  EMAIL = 'email',
  USE_FOR_NOTIFICATIONS = 'useForNotifications',
}

export enum OverdueEmailsType {
  SAME_AS_VERIFICATION_EMAILS = 'SAME_AS_VERIFICATION_EMAILS',
  DIFFERENT_EMAIL = 'DIFFERENT_EMAIL',
}

export interface ClientDebtorBillingAddressesFormValues {
  id: string
  overdueEmailsType?: OverdueEmailsType
  overdueEmails: Array<DebtorOverdueEmailDTO>
  country: string
  verificationEmails: Array<DebtorVerificationEmailDTO>
  city: string
  postalCode: string
  phones: Array<DebtorPhoneDTO>
  street1: string
  street2: string
}

export interface AddClientDebtorBillingAddressesVariables extends ClientDebtorBillingAddressesFormValues {
  clientDebtorId: string
}

export interface UpdateClientDebtorBillingAddressesData {
  updateDebtorContact: DebtorContactDTO
}

export interface UpdateClientDebtorBillingAddressesVariables extends ClientDebtorBillingAddressesFormValues {
  id: string
}

export enum ClientDebtorFileType {
  CONTRACT = 'CONTRACT',
  DEBTOR_PAYMENT_PLAN = 'DEBTOR_PAYMENT_PLAN',
  DEBTOR_STATEMENT = 'DEBTOR_STATEMENT',
  NOTIFICATION_OF_ASSIGNMENT = 'NOTIFICATION_OF_ASSIGNMENT',
  JOINT_NOTIFICATION_ASSIGNMENT = 'JOINT_NOTIFICATION_ASSIGNMENT',
  PAYMENT_HISTORY = 'PAYMENT_HISTORY',
  OTHER = 'OTHER',
}

export interface ClientDebtorReportFileDTO {
  clientDebtorId: string
  content: string
  description: string
  documentId: string
  fileName: string
  reportCreatedAt: string
  reportType: ClientDebtorFileType
  sendReportToReceivers: boolean
  uploader: string
}

export interface Page_ClientDebtorReportFileDTO {
  content: Array<ClientDebtorReportFileDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientDebtorReportsData {
  clientDebtorReports: Page_ClientDebtorReportFileDTO
}

export interface ClientDebtorReportsVariables {
  clientDebtorId: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
}

export interface DownloadClientDebtorReportData {
  downloadClientDebtorReport: ClientDebtorReportFileDTO
}

export interface DownloadClientDebtorReportVariables {
  documentId: string
}

export interface UploadClientDebtorReportData {
  uploadClientDebtorReport: string
}

export interface UploadClientDebtorReportVariables {
  reportType: ClientDebtorFileType | string
  clientDebtorId?: string
  file: ReportFileDTOInput | string
  reportCreatedAt: string
  description?: string
}

export interface UploadClientDebtorReportFormValues {
  reportType: ClientDebtorFileType | string
  clientDebtorId?: string
  files: Array<ReportFileDTOInput>
  reportCreatedAt: string
  description?: string
}

export interface DeleteClientDebtorReportData {
  deleteClientDebtorReport: ClientDebtorReportFileDTO
}

export interface DeleteClientDebtorReportVariables {
  documentId: string
}

export enum VerificationType {
  REGULAR = 'REGULAR',
  OUTSTANDING_AMOUNT = 'OUTSTANDING_AMOUNT',
  PERIODICAL = 'PERIODICAL',
  EACH_INVOICE = 'EACH_INVOICE',
  BASED_ON_DOCS_RECEIVED_FROM_DEBTORS = 'BASED_ON_DOCS_RECEIVED_FROM_DEBTORS',
  EXCEPTION = 'EXCEPTION',
}

export enum Periodicity {
  ONCE_WEEK = 'ONCE_WEEK',
  ONCE_TWO_WEEKS = 'ONCE_TWO_WEEKS',
}

export enum OutstandingAmountPeriodicity {
  ONCE_MONTH = 'ONCE_MONTH',
  ONCE_TWO_MONTHS = 'ONCE_TWO_MONTHS',
  ONCE_THREE_MONTHS = 'ONCE_THREE_MONTHS',
}

export enum PeriodicalVerificationType {
  ONCE_WEEK = 'ONCE_WEEK',
  ONCE_TWO_WEEKS = 'ONCE_TWO_WEEKS',
  ONCE_MONTH = 'ONCE_MONTH',
  ONCE_TWO_MONTHS = 'ONCE_TWO_MONTHS',
  ONCE_THREE_MONTHS = 'ONCE_THREE_MONTHS',
}

export interface InvoiceVerificationFormFields {
  verificationType: VerificationType
  periodicalVerificationType: PeriodicalVerificationType
  firstPeriodicalVerification: string
  exceptionTypeDescription: string

  applyInvoiceVerificationRule: boolean

  applyInvoiceAmountThreshold: boolean
  verificationThreshold: number
  verificationThresholdPln: number

  applyUnusualInvoiceAmount: boolean
  amountThresholdFirstNew: number
  amountThresholdFirstNewPln: number

  applyFirstDebtorInvoice: boolean
  varianceThreshold: number

  applyRandomVerification: boolean
  randomNumber: number

  showMultiCurrency?: boolean
}

export type InvoiceVerificationDetailsDTO = InvoiceVerificationFormFields

export interface InvoiceOutstandingVerificationApprovalDTO {
  id: string
  invoiceNumber: string
  outstandingVerificationId: string
  status: OutstandingVerificationStatus
  totalOutstanding: number
  verificationConfirmedAt: Date
  verificationConfirmedBy: string
  verificationRequired: Date
  verificationSent: Date
  verificationSentTo: string
}

export interface VerificationDetailsDTO {
  amountThresholdFirstExistingPln: number
  amountThresholdFirstExisting: number
  amountThresholdFirstNew: number
  amountThresholdFirstNewPln: number
  clientDebtorId: string
  comment: string
  exceptionExpiryDate: Date
  exceptionTypeDescription: string
  firstPeriodicalVerification: Date
  periodicalVerificationType: PeriodicalVerificationType
  randomNumber: number
  sendVerificationEmail: boolean
  varianceThreshold: number
  verificationThreshold: number
  verificationThresholdPln: number
  verificationType: VerificationType
  enableAdditionalOutstandingCheck: boolean
  outstandingCheckLimit: number
  outstandingCheckLimitPln: number
}

export interface InvoiceVerificationLogDTO {
  auditAction: AuditActions
  createdOn: Date
  id: string
  updatedByFullName: string
  verificationDetails: InvoiceVerificationDetailsDTO
}

export interface InvoiceVerificationDTO {
  verificationDetails: InvoiceVerificationDetailsDTO
  outstandingAmountVerifications: Array<InvoiceOutstandingVerificationApprovalDTO>
  invoiceVerificationLogs: Array<InvoiceVerificationLogDTO>
}

export interface ClientDebtorInvoiceVerificationData {
  getInvoiceVerification: InvoiceVerificationDTO
}

export interface ClientDebtorInvoiceVerificationVariables {
  clientDebtorId: string
  invoiceId?: string
}

export interface UpdateClientDebtorInvoiceVerificationData {
  updateInvoiceVerification: InvoiceVerificationDTO
}

export interface UpdateClientDebtorInvoiceVerificationVariables extends InvoiceVerificationFormFields {
  clientDebtorId: string
}

export enum UpdateInvoiceVerificationFieldsNames {
  TYPE = 'verificationType',
  PERIODICAL_TYPE = 'periodicalVerificationType',
  FIRST_VERIFICATION_DATE = 'firstPeriodicalVerification',
  DESCRIPTION = 'exceptionTypeDescription',
  APPLY_VERIFICATION_RULES = 'applyInvoiceVerificationRule',
  APPLY_INVOICE_AMOUNT_THRESHOLD = 'applyInvoiceAmountThreshold',
  THRESHOLD = 'verificationThreshold',
  THRESHOLD_PLN = 'verificationThresholdPln',
  APPLY_UNUSUAL_INVOICE_AMOUNT = 'applyUnusualInvoiceAmount',
  AMOUNT_FOR_NEW_CLIENT = 'amountThresholdFirstNew',
  AMOUNT_FOR_NEW_CLIENT_PLN = 'amountThresholdFirstNewPln',
  APPLY_FIRST_DEBTOR_INVOICE = 'applyFirstDebtorInvoice',
  VARIANCE = 'varianceThreshold',
  APPLY_RANDOM_VERIFICATION = 'applyRandomVerification',
  RANDOM = 'randomNumber',
  SHOW_MULTI_CURRENCY = 'showMultiCurrency',
}

export interface GetClientDebtorCommentsData {
  getClientDebtorComments: Array<DebtorCommentListDTO>
}

export interface GetClientDebtorCommentsVariables {
  clientDebtorId: string
}

export interface GetClientDebtorCommentByIdData {
  getClientDebtorCommentById: DebtorCommentDTO
}

export interface GetClientDebtorCommentsByIdVariables {
  commentId: string
}

export interface AddClientDebtorCommentData {
  addClientDebtorComment: DebtorCommentDTO
}

export interface AddClientDebtorCommentVariables {
  clientDebtorId: string
  comment: string
}

export interface UpdateClientDebtorCommentData {
  updateClientDebtorComment: DebtorCommentDTO
}

export interface UpdateClientDebtorCommentVariables {
  comment: string
  commentId: string
}

export interface MarkClientDebtorCommentAsFlaggedData {
  markClientDebtorCommentAsFlagged: DebtorCommentDTO
}

export interface MarkClientDebtorCommentAsFlaggedVariables {
  commentId: string
}

export interface MarkClientDebtorCommentAsUnflaggedData {
  markClientDebtorCommentAsUnflagged: DebtorCommentDTO
}

export interface MarkClientDebtorCommentAsUnflaggedVariables {
  commentId: string
}

export interface MarkClientDebtorCommentAsArchivedData {
  markClientDebtorCommentAsArchived: DebtorCommentDTO
}

export interface MarkClientDebtorCommentAsArchivedVariables {
  commentId: string
}

export interface MarkClientDebtorCommentAsUnarchivedData {
  markClientDebtorCommentAsUnarchived: DebtorCommentDTO
}

export interface MarkClientDebtorCommentAsUnarchivedVariables {
  commentId: string
}

export interface SettlementFileDTO {
  content: string
  fileName: string
}
export interface SettlementNoteDTO {
  id: string
  issueDate: Date
  noteNumber: string
  noteType: NoteType
  status: AccountingNoteStatus
  totalInclVat: number
  currency: Currency
}

export interface SettlementDetailsDTO {
  clientCompanyName: string
  clientId: string
  details: SettlementDTO
  notes: Array<SettlementNoteDTO>
}

export interface ClientSettlementDetailsData {
  clientSettlementDetails: SettlementDetailsDTO
}

export interface ClientSettlementDetailsVariables {
  settlementId: string
}

export interface InvoiceFilterDTO {
  id: string
  invoiceNumber: string
}

export interface ClientDebtorInvoicesData {
  clientDebtorInvoices: Array<InvoiceFilterDTO>
}

export interface ClientDebtorInvoicesVariables {
  clientDebtorId: string
  searchText: string
  currency: Currency
}

// Client credit note tab

export interface ClientCreditNoteListDTO {
  amount: number
  clientCreditNoteNumber: string
  clientDebtorId: string
  createdOn: Date
  createdBy: string
  description: string
  fileName: string
  id: string
  noteStatus: NoteStatus
  openAmount: number
  usedAmount: number
  invoiceNumbers: Array<ClientCreditNoteInvoiceDTO>
  currency: Currency
}

export interface ClientCreditNoteTotalsDTO {
  amount: number
  openAmount: number
  usedAmount: number
}

export interface Page_ClientCreditNoteListDTO {
  content: Array<ClientCreditNoteListDTO>
  totals: ClientCreditNoteTotalsDTO
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface ClientCreditNoteData {
  clientCreditNoteList: Page_ClientCreditNoteListDTO
}

export interface ClientCreditNoteVariables {
  clientDebtorId: string
  clientCreditNoteNumber?: string
  sortDirection?: Direction
  sortField?: string
  pageSize?: number
  pageNum?: number
  noteStatus?: Array<NoteStatus>
}

export enum NoteStatus {
  ARCHIVED = 'ARCHIVED',
  CLOSED = 'CLOSED',
  OPEN = 'OPEN',
}

export interface ClientCreditNoteInvoiceDTO {
  amount: number
  id: string
  invoiceNumber: string
  status: InvoiceStatus
}
export interface ClientCreditNoteDetailsDTO {
  amount: number
  clientCreditNoteNumber: string
  clientDebtorId: string
  createdBy: string
  createdByEmail: string
  createdOn: Date
  description: string
  fileName: string
  id: string
  invoiceNumbers: Array<ClientCreditNoteInvoiceDTO>
  noteStatus: NoteStatus
  openAmount: number
  usedAmount: number
  currency: Currency
}

export interface ArchiveClientCreditNoteData {
  archiveClientCreditNote: ClientCreditNoteDetailsDTO
}

export interface ArchiveClientCreditNoteVariables {
  clientCreditNoteId: string
}

export interface CreateClientCreditNoteData {
  createClientCreditNote: ClientCreditNoteDetailsDTO
}

export interface CreateClientCreditNoteVariables {
  clientDebtorId: string
  amount: number
  clientCreditNoteNumber: string
  invoices: Array<string>
  description: string
  file: ReportFileDTOInput
}

export interface CreateClientCreditNoteFormData {
  amount: number
  clientCreditNoteNumber: string
  invoices: Array<string>
  description: string
  file: ReportFileDTOInput
}

export enum CreateClientCreditNoteFieldNames {
  AMOUNT = 'amount',
  INVOICES = 'invoices',
  CLIENT_CREDIT_NOTE_NUMBER = 'clientCreditNoteNumber',
  DESCRIPTION = 'description',
  FILE = 'file',
}

export interface ClientCreditNoteDTO {
  clientId: string
  clientCompanyName: string
  clientDebtorId: string
  clientDebtorCompanyName: string
  clientCreditNoteDetails: ClientCreditNoteDetailsDTO
  clientCreditNoteInvoices: Array<ClientCreditNoteInvoiceDTO>
}

export interface ClientCreditNoteDetailsData {
  clientCreditNoteDetails: ClientCreditNoteDTO
}

export interface ClientCreditNoteDetailsVariables {
  clientCreditNoteId: string
}

export enum ClientCreditNoteDetailsFieldNames {
  STATUS = 'status',
  CREATED_AT = 'createdOn',
  CREATED_BY = 'createdBy',
  CLIENT_CREDIT_NOTE_NUMBER = 'clientCreditNoteNumber',
  INVOICES = 'invoiceNumbers',
  DESCRIPTION = 'description',
  AMOUNT = 'amount',
  OPEN_AMOUNT = 'openAmount',
  USED_AMOUNT = 'usedAmount',
  FILE = 'fileName',
}

export interface ClientCreditNoteFileDTO {
  content: string
  fileName: string
}

export interface DownloadClientCreditNoteData {
  downloadClientCreditNote: ClientCreditNoteFileDTO
}

export interface DownloadClientCreditNoteVariables {
  clientCreditNoteId: string
}

export interface UpdateClientCreditNoteData {
  updateClientCreditNote: ClientCreditNoteDetailsDTO
}

export interface UpdateClientCreditNoteVariables {
  description: string
  clientCreditNoteId: string
  invoiceIds: Array<string>
}

export interface ClientInvoicesData {
  clientInvoices: PageWithTotals_InvoiceDTO_InvoiceTotalsDTO
}

export interface ClientInvoicesVariables {
  clientId: string
  fundedAmount?: MinMaxFilterInput
  clientCompanyName?: string
  outstandingAmount?: MinMaxFilterInput
  dueDate?: FromToFilterInput
  factorAmount?: MinMaxFilterInput
  pageSize?: number
  invoiceAmount?: MinMaxFilterInput
  pageNum?: number
  sortDirection?: Direction
  overDue?: number
  sortField?: string
  invoiceNumber?: string
  issueDate?: FromToFilterInput
  status?: Array<InvoiceStatusFilter>
  currency?: Array<Currency>
  invoiceExternalVerificationStatus?: Array<InvoiceExternalVerificationStatusFilter>
}

export interface ApproveClientData {
  approveClient: ClientCardDetailsDTO
}

export interface ApproveClientFormValues {
  comments: string
}

export enum ApproveClientFieldsNames {
  COMMENTS = 'comments',
}

export interface ApproveClientVariables extends ApproveClientFormValues {
  clientId: string
}

export interface RiskRatiosDTO {
  lastReportDate: Date
  financialReportYear: string
  revenue: number
  netProfit: number
  netProfitRate: number
  equity: number
  equityRate: number
  currentRatio: number
  daysReceivable: number
  daysPayable: number
  dayInventory: number
  cashCycle: number
  probabilityOfDefault: number | null
  creditLimit: number
  workingCapital: number
  riskScore: number
  internationalScore: string
  spRating: string
  status: string
  dbt: number
}

export interface ClientPaymentInfoDTO {
  currency: Currency
  averageUsage: number
  outstandingOverdueExposureAmount: number
  totalFinanced: number
  totalFundedAmount: number
  totalLate: number
  totalOutstanding: number
}

export interface DebtorPaymentInfoDTO {
  currency: Currency
  averageUsage: number
  closedAverageTerm: number
  limitExpirationDate: Date
  maxOverdueClosed: number
  maxOverdueOpen: number
  outstandingOverdueExposureAmount: number
  paidByDebtorRatio: number
  totalLate: number
  totalOutstanding: number
  totalWithRiskMarks: number
}

export interface ClientRelatedCompanyDTO {
  dunId: string
  address: string
  name: string
  registrationNumber: string
  country: string
}

export interface AmlRiskLabelDTO {
  amlRisk: AmlRisk
  updatedOn: Date
}

export interface AnnualReviewSummaryRecordDTO {
  annualReviewId: string
  approvedOn: Date
  approverFullName: string
  clientId: string
}

export interface RiskDetailsDTO {
  amlRiskLabel: AmlRiskLabelDTO
  riskRatios: RiskRatiosDTO
  clientPaymentInfo?: ClientPaymentInfoDTO
  clientPaymentInfos?: Array<ClientPaymentInfoDTO>
  representativeCompanies: Array<RepresentativeCompanyDTO>
  representatives: Array<RepresentativeDTO>
  relatedCompanies: Array<ClientRelatedCompanyDTO>
  annualReviewSummary: Array<AnnualReviewSummaryRecordDTO>
}

export interface UpdateClientRepresentativesData {
  updateClientRepresentatives: RiskDetailsDTO
}

export interface UpdateClientRepresentativesVariables {
  id: string
  representatives: Array<RepresentativeDTO>
}

export interface UpdateClientRepresentativesCompaniesData {
  updateClientRepresentativeCompanies: RiskDetailsDTO
}

export interface UpdateClientRepresentativesCompaniesVariables {
  id: string
  representatives: Array<string>
}

export interface UpdateClientRelatedCompaniesData {
  updateClientRelatedCompanies: RiskDetailsDTO
}

export interface UpdateClientRelatedCompaniesVariables {
  id: string
  relatedCompanies: Array<string>
}

export interface UpdateDebtorRelatedCompaniesData {
  updateDebtorRelatedCompanies: RiskDetailsDTO
}

export interface UpdateDebtorRelatedCompaniesVariables {
  id: string
  relatedCompanies: Array<string>
}

export interface MigrateClientDebtorsCollateralsData {
  migrateClientDebtorsCollaterals: boolean
}

export interface MigrateClientDebtorsCollateralsVariables {
  migrateFromClientId: string
  migrateToClientId: string
  migrateToContractId: string
}

export interface ClientRiskDetailsData {
  clientRisk: RiskDetailsDTO
}

export interface ClientRiskDetailsVariables {
  clientId: string
}

export interface UpdateClientRiskRatiosData {
  updateClientRiskRatios: RiskDetailsDTO
}

export interface UpdateClientRiskRatiosVariables {
  clientId: string
}

// Loan book contracts
export enum LoanBookFeeType {
  FIXED_AMOUNT = 'FIXED_AMOUNT',
  PERCENTAGE = 'PERCENTAGE',
}

export enum LoanAgreementStatus {
  ACTIVE = 'ACTIVE',
  CLOSED = 'CLOSED',
  EXPIRED = 'EXPIRED',
  EXPIRING_SOON = 'EXPIRING_SOON',
  NEW = 'NEW',
}

export enum FactoringSecurityMeasureOption {
  BILL_OF_EXCHANGE = 'BILL_OF_EXCHANGE',
  GUARANTEE = 'GUARANTEE',
  INSURANCE = 'INSURANCE',
  RECEIVABLES = 'RECEIVABLES',
  SUBORDINATION = 'SUBORDINATION',
  OTHER = 'OTHER',
}
export enum FactoringSecurityMeasureType {
  COMBINED_GUARANTEE = 'COMBINED_GUARANTEE',
  COMPANY_AND_PERSONAL_BILL_OF_EXCHANGE = 'COMPANY_AND_PERSONAL_BILL_OF_EXCHANGE',
  COMPANY_BILL_OF_EXCHANGE = 'COMPANY_BILL_OF_EXCHANGE',
  COMPANY_GUARANTEE = 'COMPANY_GUARANTEE',
  NON_NOTARIAL_PLEDGE_OF_RECEIVABLES = 'NON_NOTARIAL_PLEDGE_OF_RECEIVABLES',
  NOTARIAL_PLEDGE_OF_RECEIVABLES = 'NOTARIAL_PLEDGE_OF_RECEIVABLES',
  OTHER_BILL_OF_EXCHANGE = 'OTHER_BILL_OF_EXCHANGE',
  PERSONAL_BILL_OF_EXCHANGE = 'PERSONAL_BILL_OF_EXCHANGE',
  PERSONAL_GUARANTEE = 'PERSONAL_GUARANTEE',
  INVEGA_PG3 = 'INVEGA_PG3',
  INVEGA_PGF2 = 'INVEGA_PGF2',
  INVEGA_PGP2 = 'INVEGA_PGP2',
}

export enum FactoringSecurityMeasureAmountType {
  FIXED = 'FIXED',
  UNLIMITED = 'UNLIMITED',
}

export interface FactoringSecurityMeasureDTO {
  id: string
  securityMeasureNumber: string
  securityMeasure: FactoringSecurityMeasureOption
  securityMeasureType: FactoringSecurityMeasureType
  communicationLanguage?: string
  documentDate?: string

  // Company details
  companyName?: string
  companyCode?: string
  companyAddress?: string
  companyRegistrationPlace?: string
  companyEmailAddress?: string

  // Natural person details
  naturalPersonName?: string
  naturalPersonSurname?: string
  naturalPersonAddress?: string
  naturalPersonBirthDate?: string
  naturalPersonBirthPlace?: string
  naturalPersonId?: string

  // Legal representative
  legalRepresentativeName?: string
  legalRepresentativePosition?: string
  legalRepresentativeSurname?: string

  // Amount
  amount: number
  amountInWords?: string
  amountType?: FactoringSecurityMeasureAmountType

  collateral?: number
  ltvRatio?: number
  contractId?: string
  startDate?: string
  endDate?: string
  secondaryPledge: boolean
  document?: ReportFileDTOInput
  comment?: string
}

export interface LoanBookAgreementDTO {
  canClose: boolean
  amount: number
  annualInterestRate: number
  clientCompanyCountry: string
  clientCompanyName: string
  clientId: string
  dailyIncreasedFee: number
  deMinimisAmount: number
  deMinimisRegistered: string
  deMinimisRegisteredBy: string
  deMinimisRequired: boolean
  deadline: string
  duration: number
  fundingLine: FundingLine
  id: string
  monthlyInterestRate: number
  onboardingFeeAmount: number
  onboardingFeePercent: number
  onboardingFeeType: LoanBookFeeType
  paymentDay: number
  productType: LoanBookType
  renewalFeeAmount: number
  renewalFeePercent: number
  renewalFeeType: LoanBookFeeType
  rpaNumber: string
  sourcingAgentAdministrates: string
  sourcingAgentAdministratesSpv: string
  sourcingAgentCountry: string
  sourcingAgentId: string
  sourcingAgentName: string
  startDate: string
  status: LoanAgreementStatus
  paymentTerm: number
  interestCompensatedByInvega: boolean
  securityMeasures: Array<LoanBookSecurityMeasureDTO>
  outstandingAmount: number
  availableAmount: number
  usedAmount: number
  paidAmount: number
  durationType: LoanBookDurationType
}

export interface Page_LoanBookAgreementDTO {
  content: Array<LoanBookAgreementDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookContractsData {
  loadLoanBookAgreementList: Page_LoanBookAgreementDTO
}

export interface LoanBookContractsVariables {
  clientId: string
  status?: LoanAgreementStatus[]
  productType?: LoanBookType[]
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface AddNewLoanBookContractData {
  addLoanBookAgreement: LoanBookAgreementDTO
}

export enum LoanBookDurationType {
  FIXED_BY_DURATION = 'FIXED_BY_DURATION',
  FIXED_BY_END_DATE = 'FIXED_BY_END_DATE',
}

export interface AddNewLoanBookContractFormValues {
  duration: number
  fundingLine: FundingLine
  clientId: string
  rpaNumber: string
  deadline: Date | string
  startDate: Date | string
  productType: LoanBookType
  durationType: LoanBookDurationType
}

export interface AddNewLoanBookContractVariables extends AddNewLoanBookContractFormValues {
  clientId: string
}

export enum NewLoanBookContractFieldNames {
  PRODUCT_TYPE = 'productType',
  FUNDING_LINE = 'fundingLine',
  RPA_NUMBER = 'rpaNumber',
  REPAYMENT_METHOD = 'durationType',
  START_DATE = 'startDate',
  END_DATE = 'deadline',
  DURATION = 'duration',
}

export enum LoanBookContractFieldNames {
  PRODUCT = 'productType',
  FUNDING_LINE = 'fundingLine',
  RPA_NUMBER = 'rpaNumber',
  STATUS = 'status',
  REPAYMENT_METHOD = 'durationType',
  START_DATE = 'startDate',
  END_DATE = 'deadline',
  DURATION = 'duration',
  AMOUNT = 'amount',
  USED_AMOUNT = 'usedAmount',
  AVAILABLE_AMOUNT = 'availableAmount',
  MONTHLY_INTEREST_RATE = 'monthlyInterestRate',
  ANNUAL_INTEREST_RATE = 'annualInterestRate',
  DAILY_INCREASE_FEE = 'dailyIncreasedFee',
  PAYMENT_DAY = 'paymentDay',
  PAYMENT_TERM = 'paymentTerm',
  INTEREST_COMPENSATED_BY_INVEGA = 'interestCompensatedByInvega',
  DE_MINIMIS_AMOUNT = 'deMinimisAmount',
  DE_MINIMIS_REQUIRED = 'deMinimisRequired',
  DE_MINIMIS_REGISTERED = 'deMinimisRegistered',
  DE_MINIMIS_REGISTERED_BY = 'deMinimisRegisteredBy',
  ONBOARDING_FEE_TYPE = 'onboardingFeeType',
  ONBOARDING_FEE = 'onboardingFeeAmount',
  ONBOARDING_FEE_PERCENTAGE = 'onboardingFeePercent',
  ONBOARDING_FEE_CALCULATED = 'onboardingFeeCalculated',
  RENEWAL_FEE_TYPE = 'renewalFeeType',
  RENEWAL_FEE = 'renewalFeeAmount',
  RENEWAL_FEE_PERCENTAGE = 'renewalFeePercent',
  RENEWAL_FEE_CALCULATED = 'renewalFeeCalculated',
}

export interface UpdateLoanBookContractData {
  updateLoanBookAgreement: LoanBookAgreementDTO
}

export interface UpdateLoanBookContractVariables {
  amount: number
  annualInterestRate: number
  dailyIncreasedFee: number
  deMinimisAmount: number
  deMinimisRegistered: string
  deMinimisRegisteredBy: string
  deMinimisRequired: boolean
  deadline: string
  duration: number
  fundingLine: FundingLine
  id: string
  monthlyInterestRate: number
  onboardingFeeAmount: number
  onboardingFeePercent: number
  onboardingFeeType: LoanBookFeeType
  paymentDay: number
  productType: LoanBookType
  renewalFeeAmount: number
  renewalFeePercent: number
  renewalFeeType: LoanBookFeeType
  rpaNumber: string
  startDate: string
  paymentTerm: number
  interestCompensatedByInvega: boolean
}

export interface CloseLoanBookContractData {
  closeLoanBookAgreement: LoanBookAgreementDTO
}

export interface CloseLoanBookContractVariables {
  id: string
}

export interface ActivateLoanBookContractData {
  approveLoanBookAgreement: LoanBookAgreementDTO
}

export interface ActivateLoanBookContractVariables {
  id: string
}

export enum LoanBookAgreementApprovalError {
  LOAN_BOOK_ALREADY_APPROVED = 'LOAN_BOOK_ALREADY_APPROVED',
  SECURITY_MEASURES_INSUFFICIENT = 'SECURITY_MEASURES_INSUFFICIENT',
  AGREEMENT_DETAILS_NOT_FILED = 'AGREEMENT_DETAILS_NOT_FILED',
  DEADLINE_NOT_IN_FUTURE = 'DEADLINE_NOT_IN_FUTURE',
  MISSING_SUPPLIERS = 'MISSING_SUPPLIERS',
}

export interface LoanBookAgreementApprovalErrorDTO {
  errors: Array<LoanBookAgreementApprovalError>
}

export interface CanActivateLoanBookContractData {
  canApproveLoanBookAgreement: LoanBookAgreementApprovalErrorDTO
}

export interface CanActivateLoanBookContractVariables {
  id: string
}

export interface LoanBookContractData {
  getLoanBookAgreement: LoanBookAgreementDTO
}

export interface LoanBookContractVariables {
  id: string
}

export interface FactoringSecurityMeasureFormValues {
  id?: string
  securityMeasureNumber: string
  securityMeasure: FactoringSecurityMeasureOption
  securityMeasureType: FactoringSecurityMeasureType
  communicationLanguage?: string
  documentDate?: string

  // Company details
  companyName?: string
  companyCode?: string
  companyAddress?: string
  companyRegistrationPlace?: string
  companyEmailAddress?: string

  // Natural person details
  naturalPersonName?: string
  naturalPersonSurname?: string
  naturalPersonAddress?: string
  naturalPersonBirthDate?: string
  naturalPersonBirthPlace?: string
  naturalPersonId?: string

  // Legal representative
  legalRepresentativeName?: string
  legalRepresentativePosition?: string
  legalRepresentativeSurname?: string

  // Amount
  amount: number
  amountInWords?: string
  amountType?: FactoringSecurityMeasureAmountType

  collateral?: number
  ltvRatio?: number
  contractId?: string
  startDate?: string
  endDate?: string
  secondaryPledge: boolean
  document?: ReportFileDTOInput
  comment?: string
}

export interface RemoveSecurityMeasureData {
  deleteLoanBookSecurityMeasure: boolean
}

export interface RemoveSecurityMeasureVariables {
  id: string
}

export enum CreateSecurityMeasureFieldNames {
  SECURITY_MEASURE_TYPE = 'securityMeasureType',
  SECURITY_MEASURE = 'securityMeasure',
  COMPANY_NAME = 'companyName',
  COMPANY_REGISTRATION_NUMBER = 'companyCode',
  NATURAL_PERSON_NAME = 'naturalPersonName',
  NATURAL_PERSON_SURNAME = 'naturalPersonSurname',
  AMOUNT_TYPE = 'amountType',
  AMOUNT = 'amount',
  COLLATERAL = 'collateral',
  LVT = 'ltvRatio',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SECONDARY_PLEDGE = 'secondaryPledge',
  DOCUMENT = 'document',
  COMMENT = 'comment',
}

export enum FactoringSecurityMeasureFieldNames {
  SECURITY_MEASURE_TYPE = 'securityMeasureType',
  SECURITY_MEASURE = 'securityMeasure',
  COMPANY_NAME = 'companyName',
  COMPANY_REGISTRATION_NUMBER = 'companyCode',
  NATURAL_PERSON_NAME = 'naturalPersonName',
  NATURAL_PERSON_SURNAME = 'naturalPersonSurname',
  AMOUNT_TYPE = 'amountType',
  AMOUNT = 'amount',
  COLLATERAL = 'collateral',
  LVT = 'ltvRatio',
  START_DATE = 'startDate',
  END_DATE = 'endDate',
  SECONDARY_PLEDGE = 'secondaryPledge',
  DOCUMENT = 'document',
  COMMENT = 'comment',
  AMOUNT_IN_WORD = 'amountInWords',
  COMMUNICATION_LANGUAGE = 'communicationLanguage',
  NATURAL_PERSON_BIRTH_DATE = 'naturalPersonBirthDate',
  NATURAL_PERSON_BIRTH_PLACE = 'naturalPersonBirthPlace',
  NATURAL_PERSON_ID = 'naturalPersonId',
  NATURAL_PERSON_ADDRESS = 'naturalPersonAddress',
  COMPANY_REGISTRATION_PLACE = 'companyRegistrationPlace',
  COMPANY_ADDRESS = 'companyAddress',
  COMPANY_EMAIL_ADDRESS = 'companyEmailAddress',
  LEGAL_REPRESENTATIVE_NAME = 'legalRepresentativeName',
  LEGAL_REPRESENTATIVE_SURNAME = 'legalRepresentativeSurname',
  LEGAL_REPRESENTATIVE_POSITION = 'legalRepresentativePosition',
  DOCUMENT_DATE = 'documentDate',
}

export interface SecurityMeasuresData {
  getLoanBookSecurityMeasure: LoanBookSecurityMeasureDTO
}

export interface SecurityMeasuresVariables {
  id: string
}

export interface ClientSupplierDTO {
  city: string
  companyName: string
  country: string
  dunId: string
  iban: string
  id: string
  postalCode: string
  registrationNumber: string
  street1: string
  street2: string
}

export interface Page_ClientSupplierListDTO {
  content: Array<ClientSupplierDTO>
  number: number
  size: number
  totalElements: number
  totalPages: number
}

export interface LoanBookContractSuppliersData {
  contractSupplierList: Page_ClientSupplierListDTO
}

export interface LoanBookContractSuppliersVariables {
  agreementId: string
  companyName?: string
  iban?: string
  pageSize?: number
  pageNum?: number
  sortDirection?: Direction
  sortField?: string
}

export interface AddClientSuppliersData {
  addSupplier: ClientSupplierDTO
}

export interface AddClientSuppliersVariables {
  dunId: string
  agreementId: string
  street1: string
  street2: string
  city: string
  postalCode: string
  country: string
  iban: string
}

export interface AddClientSupplierFormData {
  companyCountryCode?: string
  searchType: DUNSSearchType
  dunId: string
  street1: string
  street2: string
  city: string
  postalCode: string
  country: string
  iban: string
}

export interface UpdateClientSuppliersData {
  updateSupplier: ClientSupplierDTO
}

export interface UpdateClientSuppliersVariables {
  id: string
  street1: string
  street2: string
  city: string
  postalCode: string
  country: string
  iban: string
}

export enum ClientSuppliersFieldsNames {
  DUNS_ID = 'dunId',
  STREET_1 = 'street1',
  STREET_2 = 'street2',
  CITY = 'city',
  POSTAL_CODE = 'postalCode',
  COUNTRY = 'country',
  IBAN = 'iban',
}

export interface AssignCreditManagerToClientData {
  updateClientCreditManager: ClientCardDetailsDTO
}

export interface AssignCreditManagerToClientVariables {
  clientId: string
  creditRiskManagerEmail: string
}

export interface AssignCreditManagerToClientFormValues {
  creditRiskManagerEmail: string
}

export enum AssignCreditManagerToClientFieldsNames {
  CREDIT_RISK_MANAGER_EMAIL = 'creditRiskManagerEmail',
}

export interface FactoringContractSecurityMeasuresData {
  listFactoringContractSecurityMeasures: Array<FactoringSecurityMeasureDTO>
}

export interface FactoringContractSecurityMeasuresVariables {
  contractId: string
}

export interface AddNewFactoringContractSecurityMeasureData {
  addFactoringContractSecurityMeasure: FactoringSecurityMeasureDTO
}

export interface AddNewFactoringContractSecurityMeasureVariables extends FactoringSecurityMeasureFormValues {
  contractId: string
}

export interface EditFactoringContractSecurityMeasureData {
  updateFactoringContractSecurityMeasure: FactoringSecurityMeasureDTO
}

export interface EditFactoringContractSecurityMeasureVariables extends FactoringSecurityMeasureFormValues {
  id: string
}

export interface RemoveFactoringContractSecurityMeasureData {
  deleteFactoringContractSecurityMeasure: boolean
}

export interface RemoveFactoringContractSecurityMeasureVariables {
  id: string
}

export interface GenerateFactoringContractSecurityMeasureData {
  generateFactoringContractSecurityMeasure: ClientContractFileDTO
}

export interface GenerateFactoringContractSecurityMeasureVariables {
  securityMeasureId: string
}

export interface ClientRiskOfferDetailsDTO {
  comment: string
  includedDebtors: Array<RiskOfferDebtorDTO>
  invoiceVerification: RiskOfferInvoiceVerificationDTO
  requirementsBeforeFinancing: string
  riskOffer: RiskOfferDTO
  securityMeasure: Array<FactoringSecurityMeasureDTO>
  creditManagementPlan: CreditManagementPlanDTO
  logs: Array<RiskOfferAuditDTO>
}

export interface ClientRiskOfferDetailsData {
  loadClientRiskOfferDetails: ClientRiskOfferDetailsDTO
}

export interface ClientRiskOfferDetailsVariables {
  contractId: string
}

export interface UpdateClientRiskOfferCommentData {
  updateClientRiskOfferComment: ClientRiskOfferDetailsDTO
}

export interface UpdateClientRiskOfferCommentVariables {
  id: string
  summary: string
}

export interface UpdateClientDebtorStatementRequirementData {
  updateClientDebtorStatementRequirement: boolean
}

export interface UpdateClientDebtorStatementRequirementVariables extends DebtorStatementRequirementDTO {
  clientDebtorId: string
}

export enum ClientDebtorStatementRequirementFieldNames {
  DEBTOR_STATEMENT_REQUIRED = 'debtorStatementRequired',
  DEBTOR_STATEMENT_TYPE = 'debtorStatementType',
}

export interface SendDebtorStatementForSigningForm {
  clientEmails: Array<string>
  additionalInformation: string
  emailLanguage?: string
}

export enum SendDebtorStatementForSigningFieldNames {
  EMAILS = 'clientEmails',
  INFO = 'additionalInformation',
  LANGUAGE = 'emailLanguage',
}

export interface SendDebtorStatementForSigningVariables {
  id: string
  clientEmails: Array<string>
}

export interface SendDebtorStatementForSigningData {
  sendLoanBookDocumentForSigning: boolean
}

export interface UploadDebtorStatementSignedDocumentFormData {
  attachedDocument: ReportFileDTOInput
}

export enum UploadDebtorStatementSignedDocumentFieldsNames {
  FILE = 'attachedDocument',
}

export interface UploadDebtorStatementSignedDocumentData {
  uploadSignedClientDebtorStatement: boolean
}

export interface UploadDebtorStatementSignedDocumentVariables {
  attachedDocument: ReportFileDTOInput
  clientDebtorId: string
}

export interface DebtorStatementFormData {
  debtorStatementType: DebtorStatementType
  documentLanguage: string
  startDate: string
  agreementNumber: string
  invoices: Array<DebtorStatementInvoice>
  setOffRate: number
  gAmountApplied?: boolean
  gAmount?: number | string
}

export interface UpdateDebtorStatementData {
  updateClientDebtorStatement: boolean
}

export interface UpdateDebtorStatementVariables {
  documentLanguage: string
  debtorStatementType: DebtorStatementType
  invoices: Array<DebtorStatementInvoice>
  setOffRate: number
  clientDebtorId: string
  agreementNumber: string
  startDate: string
  gAmountApplied?: boolean
  gAmount?: number
}

export interface SendClientDebtorStatementForSigningVariables {
  clientDebtorId: string
  additionalInformation: string
  clientEmails: Array<string>
}

export interface SendClientDebtorStatementForSigningData {
  sendLoanBookDocumentForSigning: boolean
}

export interface GenerateClientDebtorStatementData {
  generateClientDebtorStatement: ProspectReportFileDTO
}

export interface GenerateClientDebtorStatementVariables {
  clientDebtorId: string
}

export enum DebtorStatementDebtorDetailsFieldsNames {
  STREET_1 = 'streetAddressLine1',
  STREET_2 = 'streetAddressLine2',
  CITY = 'city',
  POSTAL_CODE = 'postalCode',
  COUNTRY = 'country',
  COMPANY_NAME = 'debtorName',
  REGISTRATION_NUMBER = 'registrationNumber',
  VAT = 'vatNumber',
}

export interface ClientDebtorCopyResultDTO {
  failed: number
  successful: number
  failedClientDebtors: Array<ClientDebtorDTO>
}

export interface CopyClientDebtorsData {
  copyClientDebtors: ClientDebtorCopyResultDTO
}

export interface CopyClientDebtorsVariables {
  approveDebtors: boolean
  clientDebtorIds: string[]
  clientId: string
  copyAssuranceData: boolean
  contractId: string
}

export interface CopyClientDebtorsFormValues {
  approveDebtors: boolean
  clientId: string
  copyAssuranceData: boolean
  contractId: string
}

export enum CopyClientDebtorsFieldsNames {
  CLIENT_ID = 'clientId',
  CONTRACT_ID = 'contractId',
  COPY_ASSURANCE_DATE = 'copyAssuranceData',
  APPROVE_DEBTORS = 'approveDebtors',
}

export interface UpdateClientAmlRiskData {
  updateClientAmlRisk: boolean
}

export interface UpdateClientAmlRiskFormData {
  amlRisk: AmlRisk
}

export interface UpdateClientAmlRiskVariables extends UpdateClientAmlRiskFormData {
  clientId: string
}

export enum UpdateClientAmlRiskFieldValues {
  AML_RISK = 'amlRisk',
}
